import '../src/style.css';
import { withNewId, type Entity, type Vec2 } from '@lakaran/geometry';
import { Transaction } from '@lakaran/engine';
import {
  NODE_DEFS,
  OUTPUT_NODE_TYPE,
  activeParams,
  activeRefs,
  axisKind,
  bindValue,
  blankGraph,
  createNode,
  describeHistory,
  descendantsOf,
  emptyGraph,
  evaluateGraph,
  evaluateNode,
  linkNodes,
  mirrorAxis,
  newNodeId,
  nodesByCategory,
  parseRef,
  pickablePieces,
  refOptions,
  refPoint,
  removeNode,
  reserveNodeIds,
  serializeRef,
  trimEdges,
  nodeOwnEntities,
  nodeInputs,
  type NodeInputDef,
  unlinkNode,
  upstreamOf,
  valueInputs,
  valueSourceLabel,
  valueSources,
  evaluateValue,
  isValueNode,
  paramValue,
  type GraphEdge,
  type GraphNode,
  type NodeGraph,
  type NodeTextField,
  type NodeType,
} from '@lakaran/nodes';
import type { CadEditor } from '../src/editor/editor';
import type { CadRenderer } from '../src/render/renderer';
import { ICONS } from '../src/icons';
import { askChoice } from '../src/dialog';
import { createRefPicker, type RefPickRequest, type RefPicker } from './ref-picker';
import { createTrimPicker, createTrimHighlighter, type TrimPicker } from './trim-picker';
import {
  createMirrorPicker,
  createMirrorHighlighter,
  type MirrorPicker,
} from './mirror-picker';
import {
  createExcludePicker,
  createExcludeHighlighter,
  type ExcludePicker,
} from './exclude-picker';
import { createNodesScene, type NodesScene, type RowTarget } from './gpu';
import { createLinkGlow } from './link-glow';
import { createFindPicker, type FindPicker } from './find-picker';
import { CARD_WIDTH, RAIL_WIDTH } from './card-model';

// --------------------------------------------------------------------- store

type GraphMessage =
  | { kind: 'graph'; graph: NodeGraph }
  | { kind: 'hello' }
  | { kind: 'apply' }
  | { kind: 'pick'; request: RefPickRequest }
  | { kind: 'trim'; nodeId: string }
  | { kind: 'mirror'; nodeId: string }
  | { kind: 'exclude'; nodeId: string }
  | { kind: 'find' }
  | { kind: 'focus'; nodeId: string }
  | { kind: 'highlight'; nodeIds: string[] | null }
  | { kind: 'hover'; target: HoverTarget; nodeId: string; keys: string[] | null };

/**
 * Requests a window cannot serve itself (the drawing lives in the host) are forwarded over the
 * channel: the main window arms its picker, the result comes back as a normal graph update.
 */
export type GraphCommand =
  | { kind: 'apply' }
  | { kind: 'pick'; request: RefPickRequest }
  | { kind: 'trim'; nodeId: string }
  | { kind: 'mirror'; nodeId: string }
  | { kind: 'exclude'; nodeId: string }
  | { kind: 'find' }
  | { kind: 'focus'; nodeId: string }
  | { kind: 'highlight'; nodeIds: string[] | null }
  | { kind: 'hover'; target: HoverTarget; nodeId: string; keys: string[] | null };

/** Which overlay a hovered row asks the drawing window to light up. */
export type HoverTarget = 'trim' | 'mirror' | 'exclude';

/**
 * Holds the procedural graph for one window and keeps a secondary window in sync
 * over `BroadcastChannel`. The host (main editor) owns preview + apply.
 */
export class GraphStore {
  private graph: NodeGraph;
  private readonly listeners = new Set<(graph: NodeGraph) => void>();
  private readonly commandListeners = new Set<(command: GraphCommand) => void>();
  private readonly channel: BroadcastChannel | null = null;

  constructor(
    initial: NodeGraph,
    private readonly role: 'host' | 'client',
    channelName = 'lakaran.nodes',
  ) {
    this.graph = initial;
    reserveNodeIds(initial);
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(channelName);
      this.channel.onmessage = (event: MessageEvent<GraphMessage>) => this.onMessage(event.data);
    }
  }

  get current(): NodeGraph {
    return this.graph;
  }

  onChange(listener: (graph: NodeGraph) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  onCommand(listener: (command: GraphCommand) => void): () => void {
    this.commandListeners.add(listener);
    return () => this.commandListeners.delete(listener);
  }

  /** Mutate locally, then refresh listeners and any peer window. */
  update(mutator: (graph: NodeGraph) => void): void {
    mutator(this.graph);
    this.notify();
    this.broadcastGraph();
  }

  /** Position/param nudges that were already applied to the model. */
  touch(): void {
    this.notify();
    this.broadcastGraph();
  }

  replace(graph: NodeGraph, broadcast = true): void {
    this.graph = graph;
    // Loaded graphs carry their own ids; the counter has to continue after them.
    reserveNodeIds(graph);
    this.notify();
    if (broadcast) this.broadcastGraph();
  }

  requestSync(): void {
    this.channel?.postMessage({ kind: 'hello' } satisfies GraphMessage);
  }

  /** Hand this window's graph to a peer (used when a window regains focus). */
  share(): void {
    this.broadcastGraph();
  }

  sendApply(): void {
    this.channel?.postMessage({ kind: 'apply' } satisfies GraphMessage);
  }

  /** Arm the drawing window's reference picker for one field. */
  sendPick(request: RefPickRequest): void {
    this.channel?.postMessage({ kind: 'pick', request } satisfies GraphMessage);
  }

  /** Arm the drawing window's trim picker for one step. */
  sendTrim(nodeId: string): void {
    this.channel?.postMessage({ kind: 'trim', nodeId } satisfies GraphMessage);
  }

  /** Arm the drawing window's mirror picker for one step. */
  sendMirror(nodeId: string): void {
    this.channel?.postMessage({ kind: 'mirror', nodeId } satisfies GraphMessage);
  }

  /** Arm the drawing window's exclude picker for one merge or boolean step. */
  sendExclude(nodeId: string): void {
    this.channel?.postMessage({ kind: 'exclude', nodeId } satisfies GraphMessage);
  }

  /** Ask the drawing window to arm the finder — this window has no drawing to pick in. */
  sendFind(): void {
    this.channel?.postMessage({ kind: 'find' } satisfies GraphMessage);
  }

  /** Tell the node window which step the drawing window found. */
  sendFocus(nodeId: string): void {
    this.channel?.postMessage({ kind: 'focus', nodeId } satisfies GraphMessage);
  }

  /** Tell the drawing window which steps a node window is pointing at. */
  sendHighlight(nodeIds: readonly string[] | null): void {
    this.channel?.postMessage({
      kind: 'highlight',
      nodeIds: nodeIds && nodeIds.length > 0 ? [...nodeIds] : null,
    } satisfies GraphMessage);
  }

  /** Tell the drawing window which pieces a hovered row names, so it can light them up. */
  sendHover(target: HoverTarget, nodeId: string, keys: readonly string[] | null): void {
    this.channel?.postMessage({
      kind: 'hover',
      target,
      nodeId,
      keys: keys && keys.length > 0 ? [...keys] : null,
    } satisfies GraphMessage);
  }

  dispose(): void {
    this.listeners.clear();
    this.commandListeners.clear();
    this.channel?.close();
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener(this.graph);
  }

  private broadcastGraph(): void {
    this.channel?.postMessage({ kind: 'graph', graph: this.graph } satisfies GraphMessage);
  }

  private onMessage(message: GraphMessage): void {
    if (!message) return;
    switch (message.kind) {
      case 'graph':
        this.graph = message.graph;
        reserveNodeIds(message.graph);
        this.notify();
        break;
      case 'hello':
        if (this.role === 'host') this.broadcastGraph();
        break;
      case 'apply':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) listener({ kind: 'apply' });
        }
        break;
      case 'pick':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) {
            listener({ kind: 'pick', request: message.request });
          }
        }
        break;
      case 'trim':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) {
            listener({ kind: 'trim', nodeId: message.nodeId });
          }
        }
        break;
      case 'mirror':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) {
            listener({ kind: 'mirror', nodeId: message.nodeId });
          }
        }
        break;
      case 'exclude':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) {
            listener({ kind: 'exclude', nodeId: message.nodeId });
          }
        }
        break;
      case 'find':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) listener({ kind: 'find' });
        }
        break;
      case 'focus':
        if (this.role !== 'host') {
          for (const listener of [...this.commandListeners]) {
            listener({ kind: 'focus', nodeId: message.nodeId });
          }
        }
        break;
      case 'highlight':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) {
            listener({ kind: 'highlight', nodeIds: message.nodeIds });
          }
        }
        break;
      case 'hover':
        if (this.role === 'host') {
          for (const listener of [...this.commandListeners]) {
            listener({
              kind: 'hover',
              target: message.target,
              nodeId: message.nodeId,
              keys: message.keys,
            });
          }
        }
        break;
      default:
        break;
    }
  }
}

// ---------------------------------------------------------------------- view

export interface NodeEditorOptions {
  role: 'host' | 'client';
  onApply?: () => void;
  /** Host only: build steps from the drawing (the caller asks for confirmation first). */
  onGenerate?: () => void;
  onClose?: () => void;
  /** Host only: arm the viewport picker for one reference field. */
  onPickRef?: (request: RefPickRequest) => void;
  /** Host only: arm the viewport trim picker for a trim step. */
  onPickTrim?: (nodeId: string) => void;
  /** Host only: light up the given pieces in the drawing (`null` clears). */
  onHoverTrim?: (nodeId: string, keys: readonly string[] | null) => void;
  /** Host only: arm the viewport mirror picker for a mirror step. */
  onPickMirror?: (nodeId: string) => void;
  /** Opens the drawing picker for a merge step's exclusions. */
  onPickExclude?: (nodeId: string) => void;
  /** Host only: light up the pieces a picked row would drop (`null` clears). */
  onHoverMirror?: (nodeId: string, keys: readonly string[] | null) => void;
  /** Host only: light up the pieces an exclusion row toggles (`null` clears). */
  onHoverExclude?: (nodeId: string, keys: readonly string[] | null) => void;
  /** Host only: the drawing side of the node ↔ shape link — glow what these steps are responsible for. */
  onHighlightNodes?: (nodeIds: readonly string[] | null) => void;
  /** Host only: arm the drawing finder (click a shape, get its node). */
  onFindShape?: () => void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.5;
/** Slop around a card, in screen pixels, when deciding that a wire runs under it. */
const INSERT_PAD_PX = 8;

/** `pick:e0.2` → the kind of row and the key it names. */
function splitRow(rowId: string | null): [string, string] {
  if (!rowId) return ['', ''];
  const at = rowId.indexOf(':');
  return at < 0 ? [rowId, ''] : [rowId.slice(0, at), rowId.slice(at + 1)];
}
/** Minimum horizontal gap kept between a freshly spliced card and the steps beside it. */
const SPLICE_CLEARANCE = 50;
/** Row slack: rows within this distance of each other still count as "beside" the insert. */
const INSERT_GAP = 28;

/** A card's world-space box, used by the insert-separation rule. */
export interface CardBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * How far each step slides right when a card is spliced into a chain.
 *
 * Only steps that start **at or right of** the new card move — an insert never disturbs what is to
 * its left. A step on the right moves when it shares rows with the new card and its horizontal gap
 * is smaller than `clearance` (overlapping counts as a negative gap); the step's own downstream
 * chain moves with it by the same amount.
 */
export function spliceShifts(
  inserted: CardBox,
  others: ReadonlyArray<{ id: string; box: CardBox; descendants: readonly string[] }>,
  clearance = SPLICE_CLEARANCE,
  rowGap = INSERT_GAP,
): Map<string, number> {
  const moves = new Map<string, number>();
  const raise = (id: string, shift: number): void => {
    moves.set(id, Math.max(moves.get(id) ?? 0, shift));
  };

  for (const { id, box, descendants } of others) {
    if (box.left < inserted.left - 1e-6) continue;
    const sharesRows = inserted.top < box.bottom + rowGap && box.top < inserted.bottom + rowGap;
    if (!sharesRows) continue;
    const gap = box.left - inserted.right;
    if (gap >= clearance) continue;
    const shift = clearance - gap;
    raise(id, shift);
    for (const child of descendants) raise(child, shift);
  }
  return moves;
}

/** Stable identity for a wire, used to highlight the splice target while dragging. */
function edgeKey(edge: { from: string; to: string }): string {
  return `${edge.from}->${edge.to}`;
}

/** Selector for the input socket a wire lands on: the stream socket, or a declared slot's socket. */
function inputSelector(edge: { slot?: string }): string {
  return edge.slot
    ? `.port-in[data-slot="${edge.slot}"]`
    : '.port-in:not([data-param]):not([data-slot])';
}

/** One entry of the insert rail. */
type AddDef = (typeof NODE_DEFS)[NodeType];

/**
 * Fill a numeric row's `ƒ` picker with everything that can drive it. Rebuilt whenever the set of
 * steps changes, so a shape added *after* the card was drawn still shows up in the operand list.
 */
function fillValuePicker(
  picker: HTMLSelectElement,
  sources: ReturnType<typeof valueSources>,
  current: string | undefined,
  label: (id: string) => string,
): void {
  picker.replaceChildren();
  picker.title = sources.length
    ? 'Drive this value from a value step'
    : 'Add a Number or Measure step to drive this value';
  picker.disabled = sources.length === 0 && !current;
  const none = document.createElement('option');
  none.value = '';
  none.textContent = sources.length === 0 ? 'ƒ none' : 'ƒ';
  picker.append(none);
  for (const source of sources) {
    const option = document.createElement('option');
    option.value = source.id;
    option.textContent = source.label;
    picker.append(option);
  }
  if (current && !sources.some((source) => source.id === current)) {
    // The step that drove this value is gone: keep the binding visible, and clearable.
    const orphan = document.createElement('option');
    orphan.value = current;
    orphan.textContent = label(current);
    picker.append(orphan);
  }
  picker.value = current ?? '';
  picker.classList.toggle('is-bound', Boolean(current));
}

/** Steps that can be dropped *into* a wire: anything with an input except the final output. */function canSplice(node: GraphNode): boolean {
  const def = NODE_DEFS[node.type];
  return Boolean(def?.consumes) && node.type !== OUTPUT_NODE_TYPE;
}

/** Clipboard is shared by both windows so a graph can be pasted across them. */let clipboard: { nodes: GraphNode[]; edges: GraphEdge[] } | null = null;

interface Point {
  x: number;
  y: number;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (node instanceof HTMLButtonElement) node.type = 'button';
  return node;
}

function iconButton(icon: keyof typeof ICONS, label: string, title: string): HTMLButtonElement {
  const button = element('button', 'tool ghost icon');
  button.title = title;
  button.setAttribute('aria-label', label);
  button.innerHTML = ICONS[icon];
  return button;
}

function segmentHits(a: Point, b: Point, c: Point, d: Point): boolean {
  const d1 = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const d2 = (b.x - a.x) * (d.y - a.y) - (b.y - a.y) * (d.x - a.x);
  const d3 = (d.x - c.x) * (a.y - c.y) - (d.y - c.y) * (a.x - c.x);
  const d4 = (d.x - c.x) * (b.y - c.y) - (d.y - c.y) * (b.x - c.x);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq < 1e-9) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq));
  return Math.hypot(p.x - (a.x + abx * t), p.y - (a.y + aby * t));
}

/**
 * True when the wheel should scroll something inside the node editor instead of zooming:
 * the pointer is over a scrollable element (insert rail list, trim edge list).
 */
function canScrollFrom(target: Node | null): boolean {
  let element = target instanceof Element ? target : null;
  while (element) {
    const style = getComputedStyle(element);
    const scrolls = style.overflowY === 'auto' || style.overflowY === 'scroll';
    if (scrolls && element.scrollHeight > element.clientHeight + 1) return true;
    if (element.classList.contains('nodes-canvas')) break;
    element = element.parentElement;
  }
  return false;
}

/** The cubic used for every wire (links and the live drag preview). */function wirePath(from: Point, to: Point): string {
  const mid = (from.x + to.x) / 2;
  return `M ${from.x} ${from.y} C ${mid} ${from.y}, ${mid} ${to.y}, ${to.x} ${to.y}`;
}

/** Samples a stream wire (the same cubic that is drawn) for hit-testing. */
function wireSamples(from: Point, to: Point, steps = 16): Point[] {
  const mid = (from.x + to.x) / 2;
  const out: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    out.push({
      x: u * u * u * from.x + 3 * u * u * t * mid + 3 * u * t * t * mid + t * t * t * to.x,
      y: u * u * u * from.y + 3 * u * u * t * from.y + 3 * u * t * t * to.y + t * t * t * to.y,
    });
  }
  return out;
}

/** Dependency-free node editor used by both hosts (panel and secondary window). */
export class NodeEditorView {
  private readonly store: GraphStore;
  private readonly options: NodeEditorOptions;
  private readonly unsubscribe: () => void;
  private readonly root: HTMLElement;

  private readonly canvas: HTMLDivElement;
  private readonly world: HTMLDivElement;
  private readonly svg: SVGSVGElement;
  /** Cards currently on the canvas, with the content signature that built them. */
  private readonly cards = new Map<string, { element: HTMLDivElement; signature: string }>();
  /** The GPU canvas, when the panel was opened with `?nodes=gpu`. */
  private scene: NodesScene | null = null;
  private rowEditor: HTMLElement | null = null;
  private readonly countLabel: HTMLSpanElement;

  private pendingFrom: string | null = null;
  /** Cursor end of the wire being drawn, in world coordinates (snapped to a hovered input). */
  private livePoint: Point | null = null;
  /** Wire a dragged step would splice into, highlighted live while dragging. */
  private dropTarget: GraphEdge | null = null;
  private dragging = false;
  private readonly selected = new Set<string>();

  /** Canvas view: pan in screen pixels, zoom factor. */
  private scale = 1;
  private panX = 0;
  private panY = 0;
  private panning: { startX: number; startY: number; panX: number; panY: number } | null = null;
  private cutStart: Point | null = null;
  /** Rubber-band selection in world coordinates (window when dragged left→right). */
  private marquee: { start: Point; current: Point; additive: boolean; moved: boolean } | null = null;
  /** The insert rail's filter: where the keyboard lands when a step is being added. */
  private addFilter: HTMLInputElement | null = null;
  private rebuildAddList: ((query: string) => void) | null = null;
  /** World point the next inserted step lands on; `null` means the next free column. */
  private addAnchor: Point | null = null;
  /** Node ids of the last refresh, so a `ƒ` list is only refilled when the steps changed. */
  private sourceKey = '';

  constructor(root: HTMLElement, store: GraphStore, options: NodeEditorOptions) {
    this.store = store;
    this.options = options;

    root.classList.add('nodes-shell');
    root.replaceChildren();
    this.root = root;

    const header = element('header', 'nodes-header');
    header.append(element('span', 'nodes-title', 'PROCEDURAL NODES'));
    this.countLabel = element('span', 'nodes-count', '0 entities');

    const apply = element('button', 'tool nodes-apply', 'Apply');
    apply.title =
      options.role === 'host' ? 'Commit the preview to the drawing' : 'Apply in the main window';
    apply.addEventListener('click', () => this.options.onApply?.());

    const close = element('button', 'tool ghost nodes-close', '×');
    close.title = 'Close the node editor';
    close.hidden = options.role !== 'host';
    close.addEventListener('click', () => this.options.onClose?.());

    // Generating steps from an existing drawing asks first — the result is only a starting point.
    const generate = element('button', 'tool ghost nodes-generate', 'Generate');
    generate.title = 'Generate steps from the drawing (asks first — nothing is committed)';
    generate.hidden = options.role !== 'host';
    generate.addEventListener('click', () => this.options.onGenerate?.());

    header.append(this.countLabel);
    if (options.role === 'client') header.append(element('span', 'nodes-badge', 'linked window'));
    header.append(generate, apply, close);

    // Adding nodes lives on the insert rail and zoom on the wheel, so the old palette select /
    // + Node / ± / 100% row was only duplicating them.
    this.canvas = element('div', 'nodes-canvas');
    // The canvas owns the keyboard while the panel is in use, so Del/Ctrl+C act on nodes
    // instead of reaching the CAD command line.
    this.canvas.tabIndex = -1;
    this.world = element('div', 'nodes-world');
    this.svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
    this.svg.classList.add('nodes-edges');
    this.world.append(this.svg);
    this.canvas.append(this.world);
    this.canvas.title =
      'wheel zoom · middle/alt drag pan · ctrl drag cuts wires · ctrl+c/v copies · Del deletes the selection · double-click blank to fit · right-click to aim the next insert';
    this.setView(1, RAIL_WIDTH + 24, 8);

    root.append(header, this.canvas);
    // The insert rail sits on the left of the canvas, above the GPU canvas but below overlays.
    this.canvas.append(this.buildAddRail());
    // The finder sits at the top right of the canvas, under Apply: pick a shape, get its card.
    this.canvas.append(this.buildFindButton());

    this.attachViewGestures();

    // `?nodes=gpu` swaps the DOM cards for the batched canvas; the DOM editor stays the default
    // until the GPU one is proven on real drawings.
    if (new URLSearchParams(window.location.search).get('nodes') === 'gpu') {
      void createNodesScene(this.canvas, {
        onSelect: (nodeId, additive) => {
          if (!additive) this.selected.clear();
          if (additive && this.selected.has(nodeId)) this.selected.delete(nodeId);
          else this.selected.add(nodeId);
          this.refreshSelection();
        },
        onClearSelection: () => {
          this.selected.clear();
          this.refreshSelection();
        },
        onDrag: (dx, dy, done) => {
          const graph = this.store.current;
          if (dx !== 0 || dy !== 0) {
            for (const node of graph.nodes) {
              if (!this.selected.has(node.id)) continue;
              node.x += dx;
              node.y += dy;
            }
            this.scene?.sync(graph, this.selected);
          }
          if (done) this.store.touch();
        },
        onEditRow: (target) => this.openRowEditor(target),
        onAction: (nodeId, action, rowId) => {          if (action === 'trim') this.options.onPickTrim?.(nodeId);
          else if (action === 'mirror') this.options.onPickMirror?.(nodeId);
          else if (action === 'exclude') this.options.onPickExclude?.(nodeId);
          else if (action === 'restore-trim') {
            this.restoreTrim(nodeId, rowId.slice('trim:'.length));
          } else if (action === 'drop-exclude') {
            this.togglePick(nodeId, rowId.slice('exclude:'.length), false);
          } else if (action === 'clear-trim') {
            this.store.update((graph) => {
              const node = graph.nodes.find((candidate) => candidate.id === nodeId);
              if (node) delete node.hidden;
            });
          }
        },
        onPalette: () => this.beginInsert(null),
        onDelete: () => this.deleteSelection(),
        onHoverNode: (nodeId) => this.setHoveredNode(nodeId),
        onHoverRow: (nodeId, rowId) => this.hoverRow(nodeId, rowId),
      }).then((scene) => {
        this.scene = scene;
        // The DOM canvas (and any placeholder card built before the scene was ready) goes away.
        this.world.replaceChildren();
        this.cards.clear();
        this.world.style.display = 'none';
        this.canvas.classList.add('is-gpu');
        scene.sync(this.store.current, this.selected);
        // `fit` needs the panel's real size, which the first layout pass has not produced yet.
        const fitWhenLaidOut = (attempt = 0): void => {
          requestAnimationFrame(() => {
            if (!this.scene) return;
            const { clientWidth, clientHeight } = this.canvas;
            if (clientWidth < 2 || clientHeight < 2) {
              if (attempt < 20) fitWhenLaidOut(attempt + 1);
              return;
            }
            this.scene.fit();
          });
        };
        fitWhenLaidOut();
      });
    }

    this.unsubscribe = store.onChange(() => {
      if (!this.dragging) this.refresh();
    });
    this.refresh();
  }

  setEntityCount(count: number): void {
    this.countLabel.textContent = `${count} ${count === 1 ? 'entity' : 'entities'}`;
  }

  refresh(): void {
    if (this.scene) {
      this.scene.sync(this.store.current, this.selected);
    } else {
      // A card's `ƒ` lists are the one thing it draws that depends on the *whole* graph, so they
      // are refreshed whenever the set of steps changes — a kept card must still offer a shape
      // that was added after it was built.
      const sourceKey = this.store.current.nodes.map((node) => node.id).join(',');
      const sourcesChanged = sourceKey !== this.sourceKey;
      this.sourceKey = sourceKey;
      // Only the cards whose content actually changed are rebuilt: a parameter edit in one step
      // must not throw away every other card, its listeners and its option lists.
      const seen = new Set<string>();
      for (const node of this.store.current.nodes) {
        seen.add(node.id);
        const signature = this.cardSignature(node);
        const existing = this.cards.get(node.id);
        if (existing && existing.signature === signature) {
          if (sourcesChanged) this.syncValuePickers(existing.element, node);
          existing.element.style.left = `${node.x}px`;
          existing.element.style.top = `${node.y}px`;
          continue;
        }
        const element = this.buildNode(node);
        if (existing) existing.element.replaceWith(element);
        else this.world.append(element);
        this.cards.set(node.id, { element, signature });
      }
      for (const [id, card] of [...this.cards]) {
        if (seen.has(id)) continue;
        card.element.remove();
        this.cards.delete(id);
      }
      this.drawEdges();
    }
    const history = describeHistory(this.store.current)
      .map((step, index) => `${index + 1}. ${step.label}`)
      .join('\n');
    this.countLabel.title = history ? `History\n${history}` : 'No steps yet';
  }

  /** Refill a kept card's operand pickers: a step added since it was built can now drive it. */
  private syncValuePickers(card: HTMLElement, node: GraphNode): void {
    const graph = this.store.current;
    const sources = valueSources(graph, node.id);
    for (const picker of card.querySelectorAll<HTMLSelectElement>('.node-value-pick')) {
      const paramId = picker.dataset['param'];
      if (!paramId) continue;
      fillValuePicker(picker, sources, node.values?.[paramId], (id) => valueSourceLabel(graph, id));
    }
  }

  /**
   * Everything one card draws: the step's own fields, the points it is pinned to, the numbers driving
   * it, and — for the steps that list pieces — the stream it was given. Anything outside this is
   * invisible on the card, so it must not trigger a rebuild.
   */  private cardSignature(node: GraphNode): string {
    const graph = this.store.current;
    const parts: string[] = [
      node.type,
      JSON.stringify(node.params ?? {}),
      JSON.stringify(node.values ?? {}),
      JSON.stringify(node.picks ?? []),
      JSON.stringify(node.hidden ?? []),
      node.text ?? '',
    ];
    for (const [field, value] of Object.entries(node.refs ?? {})) {
      const ref = parseRef(value);
      const point = ref
        ? refPoint(graph, ref, (target) => evaluateNode(graph, target.id, '0'))
        : null;
      parts.push(
        `ref:${field}:${value}:${point ? `${point.x.toFixed(3)},${point.y.toFixed(3)}` : '-'}`,
      );
    }
    for (const [field, source] of Object.entries(node.values ?? {})) {
      parts.push(`fx:${field}:${valueSourceLabel(graph, source)}`);
    }
    const def = NODE_DEFS[node.type];
    if (def.listsEdges || def.listsPicks) {
      const upstream = upstreamOf(graph, node.id);
      const stream = upstream ? evaluateNode(graph, upstream.id, '0') : [];
      parts.push(`stream:${stream.map((entity) => entity.name ?? entity.id).join(',')}`);
    }
    if (isValueNode(node)) parts.push(`value:${evaluateValue(graph, node.id) ?? 'null'}`);
    for (const socket of nodeInputs(node)) {
      // The card names the step feeding each socket, so those wires are part of what it draws.
      const source = upstreamOf(graph, node.id, socket.slot);
      parts.push(`in:${socket.slot}:${source ? `${source.id}#${NODE_DEFS[source.type].title}` : '-'}`);
    }
    return parts.join('|');
  }

  dispose(): void {
    this.unsubscribe();
    window.removeEventListener('keydown', this.onKeyDown);
  }

  // ------------------------------------------------------------------- view

  private setView(scale: number, panX: number, panY: number): void {
    this.scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale));
    this.panX = panX;
    this.panY = panY;
    this.world.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`;
  }

  private zoomAt(factor: number, anchor?: Point): void {
    const rect = this.canvas.getBoundingClientRect();
    const point = anchor ?? { x: rect.width / 2, y: rect.height / 2 };
    const scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.scale * factor));
    // Keep the world point under the anchor fixed while zooming.
    const worldX = (point.x - this.panX) / this.scale;
    const worldY = (point.y - this.panY) / this.scale;
    this.setView(scale, point.x - worldX * scale, point.y - worldY * scale);
  }

  private toWorld(clientX: number, clientY: number): Point {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left - this.panX) / this.scale,
      y: (clientY - rect.top - this.panY) / this.scale,
    };
  }

  private attachViewGestures(): void {
    // The GPU canvas owns its own pointer and wheel handling; the DOM gestures would double up.
    if (new URLSearchParams(window.location.search).get('nodes') === 'gpu') return;
    this.canvas.addEventListener(
      'wheel',
      (event) => {
        // Inside a scrollable overlay (insert rail, edge list) the wheel scrolls it.
        if (canScrollFrom(event.target as Node)) return;
        event.preventDefault();
        const rect = this.canvas.getBoundingClientRect();
        this.zoomAt(Math.exp(-event.deltaY * 0.0015), {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
      },
      { passive: false },
    );

    this.canvas.addEventListener('pointerdown', (event) => {
      const target = event.target as Node;
      const onBackground =
        target === this.canvas || target === this.world || target === (this.svg as unknown as Node);

      if (event.button === 0) this.canvas.focus({ preventScroll: true });

      if (event.ctrlKey || event.metaKey) {
        if (event.button !== 0) return;
        event.preventDefault();
        this.cutStart = this.toWorld(event.clientX, event.clientY);
        this.capturePointer(this.canvas, event.pointerId);
        return;
      }

      if (event.button === 1 || (event.button === 0 && event.altKey)) {
        event.preventDefault();
        this.panning = {
          startX: event.clientX,
          startY: event.clientY,
          panX: this.panX,
          panY: this.panY,
        };
        this.capturePointer(this.canvas, event.pointerId);
        this.canvas.classList.add('is-panning');
        return;
      }

      if (event.button === 0 && onBackground) {
        // A click on empty space drops an armed wire instead of starting a rubber band.
        if (this.pendingFrom) {
          event.preventDefault();
          this.cancelLink();
          return;
        }
        const start = this.toWorld(event.clientX, event.clientY);
        this.marquee = {
          start,
          current: start,
          additive: event.shiftKey,
          moved: false,
        };
        this.capturePointer(this.canvas, event.pointerId);
      }
    });

    // Double-click on empty space frames every step — the canvas has no bounds to fall back on.
    this.canvas.addEventListener('dblclick', (event) => {
      const target = event.target as Node;
      const onBackground =
        target === this.canvas || target === this.world || target === (this.svg as unknown as Node);
      if (!onBackground) return;
      event.preventDefault();
      this.fitView();
    });

    // No browser menu in the node editor: right-click on blank space aims the next insert.
    this.canvas.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      const target = event.target as Node;
      const onBackground =
        target === this.canvas || target === this.world || target === (this.svg as unknown as Node);
      if (!onBackground) return;
      this.beginInsert(this.toWorld(event.clientX, event.clientY));
    });

    this.canvas.addEventListener('pointermove', (event) => {
      if (this.panning) {
        this.setView(
          this.scale,
          this.panning.panX + event.clientX - this.panning.startX,
          this.panning.panY + event.clientY - this.panning.startY,
        );
        return;
      }
      if (this.cutStart) this.drawCut(this.cutStart, this.toWorld(event.clientX, event.clientY));
      if (this.pendingFrom) this.updateLiveWire(event.clientX, event.clientY);
      if (this.marquee) {
        this.marquee.current = this.toWorld(event.clientX, event.clientY);
        const travel = Math.hypot(
          this.marquee.current.x - this.marquee.start.x,
          this.marquee.current.y - this.marquee.start.y,
        );
        if (travel * this.scale > 3) this.marquee.moved = true;
        this.drawMarquee();
      }
    });

    const endGesture = (event: PointerEvent): void => {
      if (this.panning) {
        this.panning = null;
        this.canvas.classList.remove('is-panning');
        return;
      }
      if (this.cutStart) {
        const end = this.toWorld(event.clientX, event.clientY);
        const start = this.cutStart;
        this.cutStart = null;
        this.drawCut(null);
        this.cutWires(start, end);
        return;
      }
      if (this.marquee) {
        const band = this.marquee;
        this.marquee = null;
        this.drawMarquee();
        if (band.moved) this.selectInBand(band.start, band.current, band.additive);
        else if (!band.additive) {
          // A plain click on blank space clears the selection, as before.
          this.selected.clear();
          this.refreshSelection();
        }
      }
    };
    this.canvas.addEventListener('pointerup', endGesture);
    this.canvas.addEventListener('pointercancel', endGesture);

    window.addEventListener('keydown', (event) => this.onKeyDown(event));
  }

  /** Pointer capture is best effort: synthetic or already-released pointers must not throw. */
  private capturePointer(target: HTMLElement, pointerId: number): void {
    try {
      target.setPointerCapture(pointerId);
    } catch {
      // The gesture still works while the pointer stays over the element.
    }
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target && /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
    // Only act when the key came from inside this editor (never the CAD command line).
    if (!target || !this.root.contains(target)) return;
    const mod = event.ctrlKey || event.metaKey;

    if (!mod) {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (this.selected.size === 0) return;
        event.preventDefault();
        this.deleteSelection();
      } else if (event.key === 'Escape') {
        if (this.pendingFrom) {
          event.preventDefault();
          this.cancelLink();
        } else if (this.selected.size > 0) {
          event.preventDefault();
          this.selected.clear();
          this.refreshSelection();
        }
      }
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'c' && this.selected.size > 0) {
      event.preventDefault();
      this.copySelection();
    } else if (key === 'v' && clipboard) {
      event.preventDefault();
      this.pasteClipboard();
    }
  };

  private deleteSelection(): void {
    if (this.selected.size === 0) return;
    const ids = [...this.selected];
    this.selected.clear();
    this.refreshSelection();
    this.store.update((graph) => {
      for (const id of ids) removeNode(graph, id);
    });
  }

  // ---------------------------------------------------------- copy & paste

  private copySelection(): void {
    const graph = this.store.current;
    const nodes = graph.nodes.filter((node) => this.selected.has(node.id));
    if (nodes.length === 0) return;
    const ids = new Set(nodes.map((node) => node.id));
    clipboard = {
      nodes: JSON.parse(JSON.stringify(nodes)) as GraphNode[],
      edges: graph.edges
        .filter((edge) => ids.has(edge.from) && ids.has(edge.to))
        .map((edge) => ({ ...edge })),
    };
  }

  private pasteClipboard(): void {
    if (!clipboard) return;
    const offsets = { dx: 28, dy: 28 };
    const remap = new Map<string, string>();
    const clones: GraphNode[] = clipboard.nodes.map((node) => {
      const id = newNodeId();
      remap.set(node.id, id);
      return {
        ...JSON.parse(JSON.stringify(node)),
        id,
        x: node.x + offsets.dx,
        y: node.y + offsets.dy,
      } as GraphNode;
    });

    for (const clone of clones) {
      // A copied maths step keeps reading a copied number; a source left behind stays put.
      for (const [paramId, sourceId] of Object.entries(clone.values ?? {})) {
        const next = remap.get(sourceId);
        if (next) clone.values![paramId] = next;
      }
      if (!clone.refs) continue;
      for (const [key, value] of Object.entries(clone.refs)) {
        const ref = parseRef(value);
        if (!ref) continue;
        const next = remap.get(ref.nodeId);
        if (next) clone.refs[key] = serializeRef({ ...ref, nodeId: next });
      }
    }

    this.store.update((graph) => {
      graph.nodes.push(...clones);
      for (const edge of clipboard?.edges ?? []) {
        const from = remap.get(edge.from);
        const to = remap.get(edge.to);
        if (from && to) graph.edges.push({ from, to });
      }
    });

    this.selected.clear();
    for (const clone of clones) this.selected.add(clone.id);
    this.refreshSelection();
  }

  // --------------------------------------------------------- wire gestures

  private portPoint(nodeId: string, selector: string): Point | null {
    const element = this.world.querySelector<HTMLElement>(
      `.node[data-id="${nodeId}"] ${selector}`,
    );
    if (!element) return null;
    return this.centreOf(element);
  }

  /** Centre of any element in world coordinates. */
  private centreOf(element: Element): Point {
    const worldRect = this.world.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    return {
      x: (rect.left + rect.width / 2 - worldRect.left) / this.scale,
      y: (rect.top + rect.height / 2 - worldRect.top) / this.scale,
    };
  }

  /**
   * Start pulling a wire out of an output port. The wire follows the pointer (and snaps to a
   * hovered input) so the connection is visible while it is being made; releasing on an input
   * connects, releasing on empty space cancels, and a click without dragging keeps it armed for
   * click-then-click.
   */
  private startLink(nodeId: string, port: HTMLElement, event: PointerEvent): void {
    this.pendingFrom = nodeId;
    this.canvas.dataset['linking'] = '1';
    port.classList.add('is-armed');
    this.updateLiveWire(event.clientX, event.clientY);

    const startX = event.clientX;
    const startY = event.clientY;
    this.capturePointer(port, event.pointerId);

    const move = (moveEvent: PointerEvent): void =>
      this.updateLiveWire(moveEvent.clientX, moveEvent.clientY);
    const finish = (upEvent: PointerEvent): void => {
      port.removeEventListener('pointermove', move);
      port.removeEventListener('pointerup', finish);
      port.removeEventListener('pointercancel', finish);
      try {
        port.releasePointerCapture(upEvent.pointerId);
      } catch {
        // Already released.
      }
      const dragged = Math.hypot(upEvent.clientX - startX, upEvent.clientY - startY) > 4;
      const target = this.inputPortAt(upEvent.clientX, upEvent.clientY);
      if (target && target.owner !== nodeId) {
        this.cancelLink();
        if (target.param) {
          // A value socket: bind the operand instead of linking the stream. A shape dropped here
          // binds its main measurement, so a primitive can drive `A` or `B` directly.
          const param = target.param;
          this.store.update((graph) =>
            bindValue(graph, target.owner, param, this.operandSource(graph, target.owner, nodeId)),
          );
          return;
        }
        // A declared socket (merge's A or B) takes that one wire; otherwise the stream input.
        this.store.update((graph) => linkNodes(graph, nodeId, target.owner, target.slot ?? ''));
        return;
      }
      if (dragged) this.cancelLink();
      else this.updateLiveWire(upEvent.clientX, upEvent.clientY);
    };

    port.addEventListener('pointermove', move);
    port.addEventListener('pointerup', finish);
    port.addEventListener('pointercancel', finish);
  }

  private cancelLink(): void {
    this.pendingFrom = null;
    this.livePoint = null;
    this.canvas.dataset['linking'] = '';
    this.portHighlights(null);
    for (const armed of this.root.querySelectorAll<HTMLElement>('.port.is-armed')) {
      armed.classList.remove('is-armed');
    }
    this.svg.querySelector('.node-live')?.remove();
  }

  /** The input port under a client point, with the id of the node it belongs to. */
  private inputPortAt(
    clientX: number,
    clientY: number,
  ): { port: HTMLElement; owner: string; param?: string; slot?: string } | null {
    const doc = this.root.ownerDocument;
    const hit = doc.elementFromPoint(clientX, clientY);
    const direct = hit?.closest<HTMLElement>('.node .port-in');
    if (direct) {
      const owner = direct.closest<HTMLElement>('.node')?.dataset['id'];
      const param = direct.dataset['param'];
      const slot = direct.dataset['slot'];
      if (owner) {
        return { port: direct, owner, ...(param ? { param } : {}), ...(slot ? { slot } : {}) };
      }
    }
    // Dropping anywhere on a step that has an input connects to that step's stream input.
    const card = hit?.closest<HTMLElement>('.node');
    const port = card?.querySelector<HTMLElement>('.port-in:not([data-param]):not([data-slot])');
    const owner = card?.dataset['id'];
    return port && owner ? { port, owner } : null;
  }

  /** Marks the port the wire would land on. */
  private portHighlights(target: HTMLElement | null): void {
    for (const port of this.root.querySelectorAll<HTMLElement>('.port.is-target')) {
      if (port !== target) port.classList.remove('is-target');
    }
    target?.classList.add('is-target');
  }

  private updateLiveWire(clientX: number, clientY: number): void {
    if (!this.pendingFrom) return;
    const target = this.inputPortAt(clientX, clientY);
    const valid = target && target.owner !== this.pendingFrom ? target : null;
    this.portHighlights(valid ? valid.port : null);
    const snapped = valid ? this.centreOf(valid.port) : null;
    this.livePoint = snapped ?? this.toWorld(clientX, clientY);
    this.drawLiveWire();
  }

  private drawLiveWire(): void {
    this.svg.querySelector('.node-live')?.remove();
    if (!this.pendingFrom) return;
    const port = this.world.querySelector<HTMLElement>(
      `.node[data-id="${this.pendingFrom}"] .port-out`,
    );
    port?.classList.add('is-armed');
    const from = port ? this.centreOf(port) : null;
    if (!from || !this.livePoint) return;
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', wirePath(from, this.livePoint));
    path.setAttribute('class', 'node-edge node-live');
    this.svg.append(path);
  }

  /** Ctrl-drag: any wire the gesture crosses is removed. */
  private cutWires(from: Point, to: Point): void {
    if (Math.hypot(to.x - from.x, to.y - from.y) < 4) return;
    const doomed: GraphEdge[] = [];

    for (const edge of this.store.current.edges) {
      const start = this.portPoint(edge.from, '.port-out');
      const end = this.portPoint(edge.to, inputSelector(edge));
      if (!start || !end) continue;
      const samples = wireSamples(start, end);
      for (let i = 0; i + 1 < samples.length; i++) {
        if (segmentHits(from, to, samples[i]!, samples[i + 1]!)) {
          doomed.push(edge);
          break;
        }
      }
    }

    if (doomed.length === 0) return;
    this.store.update((graph) => {
      graph.edges = graph.edges.filter((edge) => !doomed.includes(edge));
    });
  }

  private drawCut(from: Point | null, to?: Point): void {
    this.world.querySelector('.node-cut')?.remove();
    if (!from || !to) return;
    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('x1', String(from.x));
    line.setAttribute('y1', String(from.y));
    line.setAttribute('x2', String(to.x));
    line.setAttribute('y2', String(to.y));
    line.setAttribute('class', 'node-cut');
    this.world.append(line);
  }

  /**
   * Rubber band over the canvas. Dragged left→right it selects steps fully inside (window), the
   * other way it selects everything it touches (crossing) — the same rule as the viewport.
   */
  private drawMarquee(): void {
    this.world.querySelector('.node-marquee')?.remove();
    if (!this.marquee) return;
    const { start, current } = this.marquee;
    const left = Math.min(start.x, current.x);
    const top = Math.min(start.y, current.y);
    const band = element('div', current.x >= start.x ? 'node-marquee' : 'node-marquee is-crossing');
    band.style.left = `${left}px`;
    band.style.top = `${top}px`;
    band.style.width = `${Math.abs(current.x - start.x)}px`;
    band.style.height = `${Math.abs(current.y - start.y)}px`;
    this.world.append(band);
  }

  private selectInBand(start: Point, end: Point, additive: boolean): void {
    const left = Math.min(start.x, end.x);
    const right = Math.max(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const bottom = Math.max(start.y, end.y);
    const crossing = end.x < start.x;

    if (!additive) this.selected.clear();
    for (const card of this.world.querySelectorAll<HTMLElement>('.node')) {
      const id = card.dataset['id'];
      if (!id) continue;
      const box = {
        left: card.offsetLeft,
        top: card.offsetTop,
        right: card.offsetLeft + card.offsetWidth,
        bottom: card.offsetTop + card.offsetHeight,
      };
      const hits = crossing
        ? box.left <= right && box.right >= left && box.top <= bottom && box.bottom >= top
        : box.left >= left && box.right <= right && box.top >= top && box.bottom <= bottom;
      if (hits) this.selected.add(id);
      else if (!additive) this.selected.delete(id);
    }
    this.refreshSelection();
  }

  /** True when the wire segment `a→b` passes through (or within `pad` of) the card box. */
  private segmentCrossesBox(
    a: Point,
    b: Point,
    box: { left: number; top: number; right: number; bottom: number },
    pad: number,
  ): boolean {
    const left = box.left - pad;
    const right = box.right + pad;
    const top = box.top - pad;
    const bottom = box.bottom + pad;
    const inside = (p: Point): boolean =>
      p.x >= left && p.x <= right && p.y >= top && p.y <= bottom;
    if (inside(a) || inside(b)) return true;
    const corners = [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ];
    for (let i = 0; i < 4; i++) {
      if (segmentHits(a, b, corners[i]!, corners[(i + 1) % 4]!)) return true;
    }
    return false;
  }

  /**
   * The wire a dragged step is sitting on, if any. A wire counts when it runs **under the card**
   * (the card's own box, plus a few pixels of slack) — dropping a tall card "on the wire" means the
   * wire disappears behind it, not that the card's exact centre lands on the curve.
   */
  private wireUnder(graphNode: GraphNode): GraphEdge | null {
    const box = this.boxOf(graphNode);
    const pad = INSERT_PAD_PX / this.scale;
    const centre = { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 };
    let best: { edge: GraphEdge; distance: number } | null = null;

    for (const edge of this.store.current.edges) {
      // Its own wires are not a splice target: a source card must not swallow its own edge.
      if (edge.from === graphNode.id || edge.to === graphNode.id) continue;
      const start = this.portPoint(edge.from, '.port-out');
      const end = this.portPoint(edge.to, inputSelector(edge));
      if (!start || !end) continue;

      const samples = wireSamples(start, end);
      for (let i = 0; i + 1 < samples.length; i++) {
        const a = samples[i]!;
        const b = samples[i + 1]!;
        if (!this.segmentCrossesBox(a, b, box, pad)) continue;
        // Rank by how central the crossing is, so the nearest wire wins when several qualify.
        const distance = distanceToSegment(centre, a, b);
        if (!best || distance < best.distance) best = { edge, distance };
      }
    }
    return best?.edge ?? null;
  }

  /** World-space box of a card: its model origin plus the size it actually lays out at. */
  private boxOf(node: GraphNode): { left: number; top: number; right: number; bottom: number } {
    const card = this.world.querySelector<HTMLElement>(`.node[data-id="${node.id}"]`);
    const width = card?.offsetWidth || 180;
    const height = card?.offsetHeight || 60;
    return { left: node.x, top: node.y, right: node.x + width, bottom: node.y + height };
  }

  /**
   * An insert only makes room **to its right**: steps that start left of the new card never move.
   * A step on the right side slides over when it shares rows with the new card and sits closer than
   * `SPLICE_CLEARANCE` (overlapping counts), and its own downstream chain slides with it.
   */
  private separateOverlaps(inserted: GraphNode): void {
    const graph = this.store.current;
    // What feeds the new card keeps its place: the insert only pushes what comes after it.
    const ancestors = new Set<string>();
    let cursor = upstreamOf(graph, inserted.id);
    while (cursor && !ancestors.has(cursor.id)) {
      ancestors.add(cursor.id);
      cursor = upstreamOf(graph, cursor.id);
    }

    const others = graph.nodes
      .filter((node) => node.id !== inserted.id && !ancestors.has(node.id))
      .map((node) => ({
        id: node.id,
        box: this.boxOf(node),
        descendants: [...descendantsOf(graph, node.id)],
      }));
    const moves = spliceShifts(this.boxOf(inserted), others);
    if (moves.size === 0) return;

    this.store.update((next) => {
      for (const node of next.nodes) {
        const shift = moves.get(node.id);
        if (shift) node.x += shift;
      }
    });
  }

  /**
   * Dropping a step onto a wire splices it into the chain. Overlapping steps then slide right just
   * far enough to clear the new card, so a mid-chain insert reads as a flow instead of a pile.
   */
  private autoInsert(graphNode: GraphNode): void {
    if (!canSplice(graphNode)) return;

    const graph = this.store.current;
    if (graph.edges.some((edge) => edge.to === graphNode.id)) return;

    const edge = this.wireUnder(graphNode);
    if (!edge) return;
    this.store.update((next) => {
      next.edges = next.edges.filter((candidate) => candidate !== edge);
      next.edges.push({ from: edge.from, to: graphNode.id });
      next.edges.push({ from: graphNode.id, to: edge.to });
    });
    this.separateOverlaps(graphNode);
  }

  // ------------------------------------------------------------- add palette

  /**
   * Inserting a step is a docked rail on the left of the canvas rather than a menu under the
   * pointer: the list is always in the same place, filtered as you type. Right-clicking blank
   * canvas aims the next step at that point, and the rail's filter takes the focus for it.
   */
  private buildAddRail(): HTMLElement {
    const rail = element('div', 'nodes-add');
    rail.append(element('span', 'nodes-add-title', 'INSERT'));

    const filter = document.createElement('input');
    filter.type = 'text';
    filter.className = 'nodes-add-filter';
    filter.placeholder = 'Search…';
    filter.autocomplete = 'off';
    filter.spellcheck = false;

    const list = element('div', 'nodes-add-list');
    const items: HTMLButtonElement[] = [];
    let activeIndex = 0;

    const highlight = (index: number): void => {
      items.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
      const item = items[index];
      if (item) item.scrollIntoView({ block: 'nearest' });
    };

    const build = (query: string): void => {
      list.replaceChildren();
      items.length = 0;
      const needle = query.trim().toLowerCase();

      const addItem = (def: AddDef): void => {
        const item = element('button', 'nodes-add-item');
        item.dataset['type'] = def.type;
        item.title = def.description;
        item.append(element('span', 'nodes-add-name', def.title));
        item.addEventListener('click', () => this.insertStep(def.type, this.addAnchor));
        list.append(item);
        items.push(item);
      };

      if (!needle) {
        for (const group of nodesByCategory()) {
          if (group.nodes.length === 0) continue;
          list.append(element('div', 'nodes-add-group', group.label));
          for (const def of group.nodes) addItem(def);
        }
      } else {
        // Rank by how well the *name* matches, so typing `add` puts Add first and the steps that
        // only mention it in their description come last.
        const score = (def: AddDef, group: string): number => {
          const title = def.title.toLowerCase();
          if (title === needle) return 0;
          if (title.startsWith(needle)) return 1;
          if (title.includes(needle)) return 2;
          if (group.toLowerCase().includes(needle)) return 3;
          return def.description.toLowerCase().includes(needle) ? 4 : -1;
        };
        const matches: Array<{ def: AddDef; rank: number }> = [];
        for (const group of nodesByCategory()) {
          for (const def of group.nodes) {
            const rank = score(def, group.label);
            if (rank >= 0) matches.push({ def, rank });
          }
        }
        matches.sort((a, b) => a.rank - b.rank);
        for (const match of matches) addItem(match.def);
        if (matches.length === 0) {
          list.append(element('div', 'nodes-add-empty', `No node matches “${query.trim()}”`));
        }
      }
      activeIndex = 0;
      highlight(0);
    };

    filter.addEventListener('pointerdown', (event) => event.stopPropagation());
    filter.addEventListener('input', () => build(filter.value));
    filter.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        filter.value = '';
        build('');
        this.canvas.focus({ preventScroll: true });
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (items.length === 0) return;
        activeIndex =
          (activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        highlight(activeIndex);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const type = items[activeIndex]?.dataset['type'] as NodeType | undefined;
        if (type) this.insertStep(type, this.addAnchor);
      }
    });

    rail.append(filter, list);
    this.addFilter = filter;
    this.rebuildAddList = build;
    build('');
    return rail;
  }

  /**
   * The finder: click it, then click a shape in the drawing, and the step that made that shape is
   * selected and brought into view. It is the other direction of the hover glow.
   */
  private buildFindButton(): HTMLElement {
    const button = element('button', 'nodes-find', 'Find shape');
    button.title = 'Find the node behind a shape — click a shape in the drawing';
    if (!this.options.onFindShape) {
      button.disabled = true;
      button.title = 'Finding needs the window that holds the drawing';
      return button;
    }
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      button.blur();
      this.options.onFindShape?.();
    });
    return button;
  }

  /** Aim the next insert at a canvas point (right-click), and put the cursor in the filter. */  private beginInsert(at: Point | null): void {
    this.addAnchor = at;
    if (!this.addFilter) return;
    this.addFilter.value = '';
    this.rebuildAddList?.('');
    this.addFilter.focus({ preventScroll: true });
    this.addFilter.select();
  }

  private insertStep(type: NodeType, at: Point | null): void {
    const spot = at ?? this.freeSpot();
    const node = createNode(type, spot.x, spot.y);
    this.store.update((graph) => {
      graph.nodes.push(node);
      // A new step joins the end of the chain, so it reaches the drawing without wiring it first.
      // Value steps are sources, not links in the chain.
      const output = graph.nodes.find((candidate) => candidate.type === OUTPUT_NODE_TYPE);
      if (!output || isValueNode(node) || !canSplice(node)) return;
      const into = graph.edges.find((edge) => edge.to === output.id);
      graph.edges = graph.edges.filter((edge) => edge !== into);
      if (into) graph.edges.push({ from: into.from, to: node.id });
      graph.edges.push({ from: node.id, to: output.id });
    });
    this.selected.clear();
    this.selected.add(node.id);
    this.refreshSelection();
    this.addAnchor = null;
    if (this.scene) {
      // The GPU canvas keeps its own camera, so pan it to the new card instead of fitting the graph.
      this.scene.reveal(spot.x, spot.y);
      return;
    }
    const card = this.world.querySelector<HTMLElement>(`.node[data-id="${node.id}"]`);
    this.revealSpot(spot, card?.offsetWidth ?? CARD_WIDTH, card?.offsetHeight ?? 160);
  }

  /** Where an unaimed step lands: a free slot in the visible part of the canvas. */
  private freeSpot(): Point {
    const nodes = this.store.current.nodes;
    if (this.scene) {
      // The GPU canvas keeps its own camera; insert past the last step and let `fit` reveal it.
      if (nodes.length === 0) return { x: 40, y: 40 };
      return {
        x: Math.max(...nodes.map((node) => node.x)) + CARD_WIDTH + 60,
        y: Math.min(...nodes.map((node) => node.y)),
      };
    }
    const rect = this.canvas.getBoundingClientRect();
    const rail = RAIL_WIDTH + 12;
    const origin = this.toWorld(rect.left + rail + 8, rect.top + 16);
    const stepX = CARD_WIDTH + 36;
    const stepY = 260;
    const columns = Math.max(1, Math.floor((rect.width - rail - 16) / stepX));
    for (let row = 0; row < 40; row++) {
      for (let column = 0; column < columns; column++) {
        const spot = { x: origin.x + column * stepX, y: origin.y + row * stepY };
        const taken = nodes.some(
          (node) =>
            Math.abs(node.x - spot.x) < CARD_WIDTH && Math.abs(node.y - spot.y) < stepY - 40,
        );
        if (!taken) return spot;
      }
    }
    return origin;
  }

  /** Pan the smallest amount that brings a freshly inserted card fully into view. */
  private revealSpot(spot: Point, width: number, height: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const rail = RAIL_WIDTH + 12;
    const margin = 10;
    const left = spot.x * this.scale + this.panX;
    const top = spot.y * this.scale + this.panY;
    const cardWidth = width * this.scale;
    const cardHeight = height * this.scale;
    let panX = this.panX;
    let panY = this.panY;
    if (cardWidth > rect.width - rail - margin * 2) {
      panX = rail + margin - spot.x * this.scale;
    } else if (left < rail + margin) {
      panX += rail + margin - left;
    } else if (left + cardWidth > rect.width - margin) {
      panX -= left + cardWidth - (rect.width - margin);
    }
    if (cardHeight > rect.height - margin * 2) {
      panY = margin - spot.y * this.scale;
    } else if (top < margin) {
      panY += margin - top;
    } else if (top + cardHeight > rect.height - margin) {
      panY -= top + cardHeight - (rect.height - margin);
    }
    if (panX !== this.panX || panY !== this.panY) this.setView(this.scale, panX, panY);
  }

  // --------------------------------------------------------------- nodes

  private refreshSelection(): void {
    this.emitHighlight();
    if (this.scene) {
      this.scene.sync(this.store.current, this.selected);
      return;
    }
    for (const card of this.world.querySelectorAll<HTMLElement>('.node')) {
      card.classList.toggle('is-selected', this.selected.has(card.dataset['id'] ?? ''));
    }
  }

  /** Which cards the drawing should be glowing: the one under the pointer, else the selection. */
  private hoveredNode: string | null = null;
  /** The row under the pointer; while it is set, the card glow steps aside for the row's own piece. */
  private hoveredRow: string | null = null;

  private emitHighlight(): void {
    const focusCard = this.hoveredRow === null && this.hoveredNode !== null;
    const wanted = focusCard
      ? [this.hoveredNode!]
      : this.selected.size > 0
        ? [...this.selected]
        : null;
    this.options.onHighlightNodes?.(wanted);
  }

  private setHoveredNode(nodeId: string | null): void {
    if (this.hoveredNode === nodeId) return;
    this.hoveredNode = nodeId;
    this.emitHighlight();
  }

  /**
   * The row under the pointer, and the piece it names. A trim row lights the span it cut away, a
   * mirror row the piece it reflects, an exclude row the piece it leaves out — the same three
   * overlays the pickers use, without arming anything.
   */
  hoverRow(nodeId: string | null, rowId: string | null): void {
    const [kind, key] = splitRow(rowId);
    const keys = key ? [key] : null;
    const focusChanged = Boolean(key) !== (this.hoveredRow !== null);
    this.hoveredRow = key ? rowId : null;
    if (focusChanged) this.emitHighlight();
    if (!nodeId || !key) {
      this.options.onHoverTrim?.(nodeId ?? '', null);
      this.options.onHoverMirror?.(nodeId ?? '', null);
      this.options.onHoverExclude?.(nodeId ?? '', null);
      return;
    }
    if (kind === 'trim') {
      this.options.onHoverTrim?.(nodeId, keys);
      this.options.onHoverMirror?.(nodeId, null);
      this.options.onHoverExclude?.(nodeId, null);
    } else if (kind === 'pick') {
      this.options.onHoverMirror?.(nodeId, keys);
      this.options.onHoverTrim?.(nodeId, null);
      this.options.onHoverExclude?.(nodeId, null);
    } else if (kind === 'exclude') {
      this.options.onHoverExclude?.(nodeId, keys);
      this.options.onHoverTrim?.(nodeId, null);
      this.options.onHoverMirror?.(nodeId, null);
    } else {
      this.options.onHoverTrim?.(nodeId, null);
      this.options.onHoverMirror?.(nodeId, null);
      this.options.onHoverExclude?.(nodeId, null);
    }
  }

  /** Select one step and pan it into view — the landing end of the drawing finder. */
  selectAndReveal(nodeId: string): void {
    const node = this.store.current.nodes.find((candidate) => candidate.id === nodeId);
    if (!node) return;
    this.selected.clear();
    this.selected.add(nodeId);
    this.hoveredNode = null;
    this.refreshSelection();
    if (this.scene) {
      this.scene.reveal(node.x, node.y);
      return;
    }
    const card = this.world.querySelector<HTMLElement>(`.node[data-id="${nodeId}"]`);
    this.revealSpot({ x: node.x, y: node.y }, card?.offsetWidth ?? CARD_WIDTH, card?.offsetHeight ?? 160);
    card?.classList.add('is-found');
    window.setTimeout(() => card?.classList.remove('is-found'), 900);
  }

  private buildNode(graphNode: GraphNode): HTMLDivElement {
    const def = NODE_DEFS[graphNode.type];
    const node = element('div', 'node');
    node.dataset['id'] = graphNode.id;
    // Hovering a card glows its shape in the drawing: the card is the shape.
    node.addEventListener('pointerenter', () => this.setHoveredNode(graphNode.id));
    node.addEventListener('pointerleave', () => this.setHoveredNode(null));
    if (graphNode.type === OUTPUT_NODE_TYPE) node.classList.add('is-output');
    if (this.selected.has(graphNode.id)) node.classList.add('is-selected');
    node.style.left = `${graphNode.x}px`;
    node.style.top = `${graphNode.y}px`;

    const head = element('div', 'node-head');
    head.append(element('span', 'node-name', def.title));
    if (isValueNode(graphNode)) {
      node.classList.add('is-value');
      const result = evaluateValue(this.store.current, graphNode.id) ?? 0;
      head.append(element('span', 'node-value', `= ${Math.round(result * 1000) / 1000}`));
    }
    const remove = element('button', 'node-del', '×');
    remove.title = 'Delete node';
    remove.addEventListener('click', (event) => {
      event.stopPropagation();
      this.selected.delete(graphNode.id);
      this.store.update((graph) => removeNode(graph, graphNode.id));
    });
    head.append(remove);

    const body = element('div', 'node-body');
    const params = activeParams(graphNode);
    const refDefs = activeRefs(graphNode);
    if (params.length === 0 && refDefs.length === 0) {
      body.append(element('div', 'node-hint', def.description));
    }

    if (refDefs.length > 0) body.append(this.buildRefPickers(graphNode));

    // Annotation steps carry a string as well as numbers.
    if (def.text) body.append(this.buildTextContent(graphNode, def.text));

    // Trim keeps every span; the pieces are picked in the drawing, the card only reports them.
    if (def.listsEdges) body.append(this.buildTrimControls(graphNode));
    // Mirror reflects either everything upstream or only the pieces picked in the drawing.
    if (def.listsPicks) body.append(this.buildMirrorControls(graphNode));
    // Merge and Boolean leave out whatever was picked in the drawing; those pieces pass through.
    if (def.listsExcludes) body.append(this.buildExcludeControls(graphNode));

    // When a reference is picked the constraint owns the position: X/Y become read-outs.
    const derivedPoints = this.derivedPoints(graphNode);
    const valueInputIds = new Set(valueInputs(graphNode).map((param) => param.id));

    for (const paramDef of params) {
      const row = element('label', 'node-param');
      row.append(element('span', undefined, paramDef.label));
      if (paramDef.choices) {
        const select = element('select', 'node-select');
        for (const choice of paramDef.choices) {
          const option = document.createElement('option');
          option.value = String(choice.value);
          option.textContent = choice.label;
          select.append(option);
        }
        select.value = String(Math.round(graphNode.params[paramDef.id] ?? paramDef.value));
        select.addEventListener('change', () => {
          const next = Number(select.value);
          this.store.update((graph) => {
            const target = graph.nodes.find((candidate) => candidate.id === graphNode.id);
            if (target) target.params[paramDef.id] = next;
          });
        });
        row.append(select);
        body.append(row);
        continue;
      }

      const boundTo = graphNode.values?.[paramDef.id];
      const derived = derivedPoints.get(paramDef.id);
      const isDerived = derived !== undefined;
      const input = document.createElement('input');
      input.type = 'number';
      input.step =
        paramDef.step !== undefined
          ? String(paramDef.step)
          : paramDef.unit === 'num'
            ? '1'
            : paramDef.unit === 'deg'
              ? '5'
              : '1';
      if (boundTo) {
        const driven = paramValue(this.store.current, graphNode, paramDef.id, paramDef.value);
        input.value = String(Math.round(driven * 1000) / 1000);
        input.disabled = true;
        input.title = `Driven by ${valueSourceLabel(this.store.current, boundTo)} — clear the ƒ picker to type a number again`;
        row.classList.add('is-driven');
      } else if (isDerived) {
        input.value = derived.toFixed(3);
        input.disabled = true;
        input.title = 'Driven by the reference — clear the picker to type a coordinate';
        row.classList.add('is-derived');
      } else {
        input.value = String(graphNode.params[paramDef.id] ?? paramDef.value);
        input.addEventListener('change', () => {
          const parsed = Number(input.value);
          if (!Number.isFinite(parsed)) {
            input.value = String(graphNode.params[paramDef.id] ?? paramDef.value);
            return;
          }
          const next = paramDef.min !== undefined ? Math.max(paramDef.min, parsed) : parsed;
          input.value = String(next);
          this.store.update((graph) => {
            const target = graph.nodes.find((candidate) => candidate.id === graphNode.id);
            if (target) target.params[paramDef.id] = next;
          });
        });
      }
      row.append(input);

      // Any value step can drive this number — a maths step or a measurement of another shape.
      const picker = element('select', 'node-select node-value-pick');
      picker.dataset['param'] = paramDef.id;
      fillValuePicker(picker, valueSources(this.store.current, graphNode.id), boundTo, (id) =>
        valueSourceLabel(this.store.current, id),
      );
      picker.addEventListener('change', () => {
        this.store.update((graph) => {
          const target = graph.nodes.find((candidate) => candidate.id === graphNode.id);
          if (!target) return;
          if (!picker.value) {
            if (target.values) delete target.values[paramDef.id];
            return;
          }
          target.values = { ...(target.values ?? {}), [paramDef.id]: picker.value };
        });
      });
      row.append(picker);
      // A maths step's operand is a socket on the input side of the card.
      if (valueInputIds.has(paramDef.id)) {
        row.prepend(this.buildValuePort(graphNode.id, paramDef.id, boundTo));
      }
      body.append(row);
    }

    head.addEventListener('pointerdown', (event) => {
      if ((event.target as HTMLElement).closest('.node-del')) return;
      // Only the left button drags a step: middle-drag and Alt+drag belong to the canvas pan,
      // even when the pointer happens to be over a card.
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey) return;
      event.preventDefault();
      // The canvas keeps focus, so node shortcuts never reach the CAD command line.
      this.canvas.focus({ preventScroll: true });

      if (event.shiftKey) {
        if (this.selected.has(graphNode.id)) this.selected.delete(graphNode.id);
        else this.selected.add(graphNode.id);
      } else if (!this.selected.has(graphNode.id)) {
        this.selected.clear();
        this.selected.add(graphNode.id);
      }
      this.refreshSelection();

      this.capturePointer(head, event.pointerId);
      const startX = event.clientX;
      const startY = event.clientY;
      const moving = [...this.selected]
        .map((id) => this.store.current.nodes.find((candidate) => candidate.id === id))
        .filter((candidate): candidate is GraphNode => Boolean(candidate))
        .map((candidate) => ({ node: candidate, x: candidate.x, y: candidate.y }));
      this.dragging = true;

      const move = (moveEvent: PointerEvent): void => {
        const dx = (moveEvent.clientX - startX) / this.scale;
        const dy = (moveEvent.clientY - startY) / this.scale;
        for (const entry of moving) {
          // Free canvas: steps may sit at negative coordinates, the view just pans to them.
          entry.node.x = entry.x + dx;
          entry.node.y = entry.y + dy;
          const target = this.world.querySelector<HTMLElement>(`.node[data-id="${entry.node.id}"]`);
          if (target) {
            target.style.left = `${entry.node.x}px`;
            target.style.top = `${entry.node.y}px`;
          }
        }
        // Show the wire this step would splice into before it is dropped.
        this.dropTarget = null;
        if (moving.length === 1 && canSplice(graphNode)) {
          this.dropTarget = this.wireUnder(graphNode);
        }
        this.drawEdges();
      };
      const finish = (): void => {
        head.removeEventListener('pointermove', move);
        head.removeEventListener('pointerup', finish);
        head.removeEventListener('pointercancel', finish);
        this.dragging = false;
        this.dropTarget = null;
        this.store.touch();
        if (moving.length === 1) this.autoInsert(graphNode);
        this.drawEdges();
      };
      head.addEventListener('pointermove', move);
      head.addEventListener('pointerup', finish);
      head.addEventListener('pointercancel', finish);
    });

    node.append(head, body);

    if (isValueNode(graphNode)) {
      // Maths steps sit beside the stream: their number flows along its own wire, into an operand.
      const outPort = element('span', 'port port-out port-value-out');
      outPort.title = 'Value — drag onto A or B of another maths step';
      outPort.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        if (this.pendingFrom === graphNode.id) this.cancelLink();
        else this.startLink(graphNode.id, outPort, event);
      });
      node.append(outPort);
      return node;
    }

    if (def.type !== OUTPUT_NODE_TYPE) {
      const outPort = element('span', 'port port-out');
      outPort.title = def.inputs
        ? 'C — the combined result, drag to an input, or click then click an input'
        : 'Output — drag to an input, or click then click an input';
      outPort.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        if (this.pendingFrom === graphNode.id) this.cancelLink();
        else this.startLink(graphNode.id, outPort, event);
      });
      node.append(outPort);
    }

    // Steps that declare sockets (merge's A and B) carry them as rows on the input edge instead.
    if (def.inputs) body.prepend(this.buildInputRows(graphNode, def.inputs));

    if (def.consumes && !def.inputs) {
      const inPort = element('span', 'port port-in');
      inPort.title = 'Input — drop a wire here, or click with an output armed (click to unlink)';
      inPort.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        const from = this.pendingFrom;
        this.cancelLink();
        if (from && from !== graphNode.id) {
          this.store.update((graph) => linkNodes(graph, from, graphNode.id));
        } else {
          this.store.update((graph) => unlinkNode(graph, graphNode.id));
        }
      });
      node.append(inPort);
    }

    return node;
  }

  /**
   * The one DOM widget the GPU canvas needs: a control placed over the focused row. Everything else
   * — the row text, its value, whether it is bound — is drawn by the canvas, so there is no field
   * DOM while nothing is being edited.
   */
  private openRowEditor(target: RowTarget): void {
    this.closeRowEditor();
    const graph = this.store.current;
    const node = graph.nodes.find((candidate) => candidate.id === target.nodeId);
    if (!node) return;
    const { row } = target;
    if (row.kind === 'badge' || row.kind === 'button' || row.kind === 'list') return;

    const wrap = element('div', 'node-row-editor');
    wrap.style.left = `${target.screen.x}px`;
    wrap.style.top = `${target.screen.y}px`;
    wrap.style.width = `${Math.max(120, target.screen.width)}px`;
    wrap.style.height = `${Math.max(22, target.screen.height)}px`;

    const commit = (): void => {
      this.closeRowEditor();
      this.store.touch();
    };

    if (row.kind === 'ref') {
      const def = activeRefs(node).find((candidate) => candidate.id === row.id);
      const select = element('select', 'node-select');
      const options = refOptions(
        graph,
        node.id,
        (id) => evaluateNode(graph, id, '0'),
        def?.kinds,
      );
      const none = document.createElement('option');
      none.value = '';
      none.textContent = '— none —';
      select.append(none);
      for (const option of options) {
        const item = document.createElement('option');
        item.value = option.value;
        item.textContent = option.label;
        select.append(item);
      }
      select.value = node.refs?.[row.id] ?? '';
      select.addEventListener('change', () => {
        const value = select.value;
        this.store.update((next) => {
          const target = next.nodes.find((candidate) => candidate.id === node.id);
          if (!target) return;
          target.refs ??= {};
          if (value) target.refs[row.id] = value;
          else delete target.refs[row.id];
        });
        commit();
      });
      wrap.append(select);
    } else if (row.kind === 'choice') {
      const def = activeParams(node).find((candidate) => candidate.id === row.id);
      const select = element('select', 'node-select');
      for (const choice of def?.choices ?? []) {
        const item = document.createElement('option');
        item.value = String(choice.value);
        item.textContent = choice.label;
        select.append(item);
      }
      select.value = String(Math.round(node.params[row.id] ?? def?.value ?? 0));
      select.addEventListener('change', () => {
        const value = Number(select.value);
        this.store.update((next) => {
          const target = next.nodes.find((candidate) => candidate.id === node.id);
          if (target) target.params[row.id] = value;
        });
        commit();
      });
      wrap.append(select);
    } else if (row.kind === 'text') {
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'node-input';
      input.value = node.text ?? '';
      input.addEventListener('change', () => {
        const value = input.value;
        this.store.update((next) => {
          const target = next.nodes.find((candidate) => candidate.id === node.id);
          if (target) target.text = value;
        });
        commit();
      });
      wrap.append(input);
    } else {
      const def = activeParams(node).find((candidate) => candidate.id === row.id);
      const input = document.createElement('input');
      input.type = 'number';
      input.className = 'node-input';
      input.step =
        def?.step !== undefined ? String(def.step) : def?.unit === 'deg' ? '5' : def?.unit === 'num' ? '1' : '1';
      input.value = row.text;
      input.addEventListener('change', () => {
        const parsed = Number(input.value);
        if (!Number.isFinite(parsed)) return;
        const value = def?.min !== undefined ? Math.max(def.min, parsed) : parsed;
        this.store.update((next) => {
          const target = next.nodes.find((candidate) => candidate.id === node.id);
          if (!target) return;
          target.params[row.id] = value;
          // Typing a number is a way of releasing a binding, like the DOM card's ƒ picker.
          if (target.values) delete target.values[row.id];
        });
        commit();
      });
      wrap.append(input);

      const sources = valueSources(graph, node.id);
      if (sources.length > 0) {
        const picker = element('select', 'node-select node-value-pick');
        const none = document.createElement('option');
        none.value = '';
        none.textContent = 'ƒ';
        picker.append(none);
        for (const source of sources) {
          const item = document.createElement('option');
          item.value = source.id;
          item.textContent = source.label;
          picker.append(item);
        }
        picker.value = node.values?.[row.id] ?? '';
        picker.title = 'Drive this value from a value step';
        picker.addEventListener('change', () => {
          const from = picker.value;
          this.store.update((next) => bindValue(next, node.id, row.id, from || null));
          commit();
        });
        wrap.append(picker);
      }
    }

    wrap.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.closeRowEditor();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const control = wrap.querySelector('input, select') as HTMLInputElement | null;
        control?.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    wrap.addEventListener('pointerdown', (event) => event.stopPropagation());

    this.canvas.append(wrap);
    this.rowEditor = wrap;
    const focusTarget = wrap.querySelector('input, select') as HTMLElement | null;
    focusTarget?.focus({ preventScroll: true });
    (focusTarget as HTMLInputElement | null)?.select?.();
  }

  private closeRowEditor(): void {
    this.rowEditor?.remove();
    this.rowEditor = null;
  }

  /** The text step's content: stored beside the numbers, so `params` stays numeric. */
  private buildTextContent(graphNode: GraphNode, field: NodeTextField): HTMLElement {    const row = element('label', 'node-param');
    row.append(element('span', undefined, field.label));
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'node-text-input';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.value = graphNode.text ?? field.value;
    input.addEventListener('change', () => {
      const value = input.value;
      this.store.update((graph) => {
        const target = graph.nodes.find((node) => node.id === graphNode.id);
        if (target) target.text = value;
      });
    });
    row.append(input);
    return row;
  }

  /**
   * The declared input sockets of a step — merge's **A** and **B**. They live on the card's input
   * edge, one row each, and each shows which step feeds it: C is what A + B makes.
   */
  private buildInputRows(graphNode: GraphNode, inputs: readonly NodeInputDef[]): HTMLElement {
    const graph = this.store.current;
    const wrap = element('div', 'node-inputs');
    for (const socket of inputs) {
      const source = upstreamOf(graph, graphNode.id, socket.slot);
      const row = element('label', 'node-param');
      const port = element('span', 'port port-in port-param port-slot');
      port.dataset['slot'] = socket.slot;
      port.title = source
        ? `${socket.label} — wired from ${NODE_DEFS[source.type].title}; click to unlink`
        : `${socket.label} — drop a shape here, or click an output then click here`;
      if (source) port.classList.add('is-bound');
      port.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        const from = this.pendingFrom;
        this.cancelLink();
        this.store.update((next) => {
          if (from && from !== graphNode.id) {
            linkNodes(next, from, graphNode.id, socket.slot);
            return;
          }
          unlinkNode(next, graphNode.id, socket.slot);
        });
      });

      row.append(port, element('span', undefined, socket.label));
      const state = element(
        'span',
        'node-input-state',
        source ? NODE_DEFS[source.type].title : '— none —',
      );
      if (source) state.classList.add('is-wired');
      row.append(state);
      wrap.append(row);
    }
    return wrap;
  }

  /**
   * The socket for one operand of a maths step. Dragging a wire from another maths step's value
   * output binds it; a *shape* dropped here binds that shape's main measurement (its width, radius
   * or length), because that is what "use this shape" means on an operand.
   */
  private buildValuePort(nodeId: string, paramId: string, boundTo: string | undefined): HTMLElement {
    const port = element('span', 'port port-in port-param');
    port.dataset['param'] = paramId;
    port.title = boundTo
      ? `Driven by ${valueSourceLabel(this.store.current, boundTo)} — click to clear`
      : 'Operand — drag a value step or a shape here, or use the ƒ picker';
    if (boundTo) port.classList.add('is-bound');
    port.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.stopPropagation();
      const from = this.pendingFrom;
      this.cancelLink();
      this.store.update((graph) =>
        bindValue(graph, nodeId, paramId, from ? this.operandSource(graph, nodeId, from) : null),
      );
    });
    return port;
  }

  /** What a wire from `from` binds: a value step as it is, a shape as its main measurement. */
  private operandSource(graph: NodeGraph, forNodeId: string, from: string): string | null {
    if (from === forNodeId) return null;
    const node = graph.nodes.find((candidate) => candidate.id === from);
    if (!node) return null;
    if (isValueNode(node)) return from;
    const derived = valueSources(graph, forNodeId).find((source) =>
      source.id.startsWith(`measure:${from}:`),
    );
    return derived?.id ?? null;
  }

  /** World point a primitive is constrained to, or `null` when it uses its own X/Y. */
  /**
   * Points a step takes from a reference. When the pick is set the constraint owns the position,
   * so the matching X/Y params become read-outs instead of editable numbers.
   */
  private derivedPoints(graphNode: GraphNode): Map<string, number> {
    const graph = this.store.current;
    const out = new Map<string, number>();
    const pairs: Array<[string, string, string]> = [
      ['reference', 'ox', 'oy'],
      ['from', 'x1', 'y1'],
      ['to', 'x2', 'y2'],
    ];
    for (const [refId, xId, yId] of pairs) {
      const ref = parseRef(graphNode.refs?.[refId]);
      if (!ref) continue;
      const point = refPoint(graph, ref, (node) => evaluateNode(graph, node.id, '0'));
      if (!point) continue;
      out.set(xId, point.x);
      out.set(yId, point.y);
    }
    return out;
  }

  /** Pickers that choose *which origin, vertex, centre or midline* to work from. */
  private buildRefPickers(graphNode: GraphNode): HTMLElement {
    const wrap = element('div', 'node-picker');
    const graph = this.store.current;
    // One evaluation per step for the whole card: solving constraints repeats the graph, so a
    // picker with three ref fields would otherwise pay for it three times over.
    const streams = new Map<string, Entity[]>();
    const evaluate = (nodeId: string): Entity[] => {
      let stream = streams.get(nodeId);
      if (!stream) {
        stream = evaluateNode(graph, nodeId, '0');
        streams.set(nodeId, stream);
      }
      return stream;
    };

    for (const refDef of activeRefs(graphNode)) {
      const options = refOptions(graph, graphNode.id, evaluate, refDef.kinds);
      const row = element('label', 'node-param');
      row.append(element('span', undefined, refDef.label));

      const select = element('select', 'node-select');
      const current = graphNode.refs?.[refDef.id] ?? '';
      if (options.length === 0) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = 'no steps yet';
        select.append(option);
        select.disabled = true;
      } else {
        const none = document.createElement('option');
        none.value = '';
        none.textContent = '— none —';
        select.append(none);
        for (const option of options) {
          const item = document.createElement('option');
          item.value = option.value;
          item.textContent = option.label;
          select.append(item);
        }
        if (current && !options.some((option) => option.value === current)) {
          const stale = document.createElement('option');
          stale.value = current;
          stale.textContent = `${current} (missing)`;
          select.append(stale);
        }
        select.value = current;
      }

      select.addEventListener('change', () => {
        const value = select.value;
        this.store.update((target) => {
          const node = target.nodes.find((candidate) => candidate.id === graphNode.id);
          if (!node) return;
          node.refs ??= {};
          if (value) node.refs[refDef.id] = value;
          else delete node.refs[refDef.id];
        });
      });

      row.append(select);

      // Pick the point in the drawing instead of hunting for it in the list.
      const pick = element('button', 'node-pick', '⊕');
      if (!this.options.onPickRef) {
        pick.disabled = true;
        pick.title = 'Picking needs the window that holds the drawing';
      } else {
        pick.title = `Pick ${refDef.label} in the drawing`;
        pick.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();
          this.options.onPickRef?.({
            nodeId: graphNode.id,
            refId: refDef.id,
            label: refDef.label,
            kinds: refDef.kinds,
          });
        });
      }
      row.append(pick);
      wrap.append(row);
    }

    return wrap;
  }

  /**
   * Trim is picked in the drawing, not ticked in a list: the card only reports what is currently
   * trimmed, offers the picker and a one-click restore. The keys live on the node (`hidden`), so
   * nothing is destroyed and restoring is just clearing the list.
   */
  private buildTrimControls(graphNode: GraphNode): HTMLElement {
    const wrap = element('div', 'node-edges');
    const edges = trimEdges(this.store.current, graphNode.id, '0');
    const trimmed = edges.filter((edge) => !edge.kept);

    const head = element('div', 'node-edges-head');
    const plural = edges.length === 1 ? 'edge' : 'edges';
    head.append(
      element(
        'span',
        'node-edges-count',
        edges.length === 0
          ? 'no edges yet'
          : trimmed.length === 0
            ? `${edges.length} ${plural} · nothing trimmed`
            : `${trimmed.length} of ${edges.length} trimmed`,
      ),
    );
    const restore = element('button', 'node-edges-reset', 'restore');
    restore.title = 'Untrim everything — the operation is non-destructive';
    restore.disabled = trimmed.length === 0;
    restore.addEventListener('click', (event) => {
      event.stopPropagation();
      this.store.update((graph) => {
        const node = graph.nodes.find((candidate) => candidate.id === graphNode.id);
        if (node) delete node.hidden;
      });
    });
    head.append(restore);
    wrap.append(head);

    if (edges.length === 0) {
      wrap.append(element('div', 'node-hint', 'link a stream in, then trim it in the drawing'));
      return wrap;
    }

    if (trimmed.length > 0) {
      const list = element('div', 'node-trim-list');
      for (const edge of trimmed) {
        const row = element('div', 'node-trim-row');
        row.append(element('span', 'node-trim-key', edge.label));
        row.append(element('span', 'node-trim-source', edge.source));
        const cancel = element('button', 'node-trim-x', '×');
        cancel.title = `Put ${edge.label} back`;
        cancel.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();
          this.restoreTrim(graphNode.id, edge.key);
        });
        row.append(cancel);
        // Hover shows exactly which span this row is talking about.
        row.addEventListener('pointerenter', () => this.options.onHoverTrim?.(graphNode.id, [edge.key]));
        row.addEventListener('pointerleave', () => this.options.onHoverTrim?.(graphNode.id, null));
        list.append(row);
      }
      wrap.append(list);

      const all = element('button', 'node-edges-reset', `show all (${trimmed.length})`);
      all.title = 'Open the full list of trimmed spans';
      all.addEventListener('click', (event) => {
        event.stopPropagation();
        this.openTrimWindow(graphNode);
      });
      wrap.append(all);
    }

    const pick = element('button', 'node-trim-pick', 'Trim in drawing');
    pick.title = 'Pick the pieces to cut away in the viewport (X or Esc ends)';
    if (!this.options.onPickTrim) {
      pick.disabled = true;
      pick.title = 'Trim picking needs the window that holds the drawing';
    } else {
      pick.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.options.onPickTrim?.(graphNode.id);
      });
    }
    wrap.append(pick);
    return wrap;
  }

  /** Puts one span back on the stream (the same edit `restore` does, for a single key). */
  private restoreTrim(nodeId: string, key: string): void {
    this.store.update((graph) => {
      const node = graph.nodes.find((candidate) => candidate.id === nodeId);
      if (!node) return;
      const hidden = new Set(node.hidden ?? []);
      hidden.delete(key);
      if (hidden.size > 0) node.hidden = [...hidden];
      else delete node.hidden;
    });
    this.options.onHoverTrim?.(nodeId, null);
  }

  /**
   * Mirror's selection. "Previous shape" reflects everything that arrives, so the card only
   * reports it — there is nothing to add. "Picked pieces" reflects only what was clicked in the
   * drawing, so that mode gets the pick button and the list. Picks live on the node (`picks`).
   */
  private buildMirrorControls(graphNode: GraphNode): HTMLElement {
    const wrap = element('div', 'node-edges');
    const graph = this.store.current;
    const pieces = pickablePieces(graph, graphNode.id, '0');
    const picked = pieces.filter((piece) => piece.picked);
    const manual = Math.round(graphNode.params['source'] ?? 0) >= 1;
    const plural = pieces.length === 1 ? 'piece' : 'pieces';

    const head = element('div', 'node-edges-head');
    head.append(
      element(
        'span',
        'node-edges-count',
        pieces.length === 0
          ? 'no pieces yet'
          : manual
            ? `${picked.length} of ${pieces.length} ${plural} picked`
            : `all ${pieces.length} ${plural} · previous shape`,
      ),
    );
    if (manual) {
      const clear = element('button', 'node-edges-reset', 'clear');
      clear.title = 'Drop every pick — the shape itself is untouched';
      clear.disabled = picked.length === 0;
      clear.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setPicks(graphNode.id, []);
      });
      head.append(clear);
    }
    wrap.append(head);
    wrap.append(element('div', 'node-hint', this.axisSummary(graphNode)));

    if (pieces.length === 0) {
      wrap.append(element('div', 'node-hint', 'link a stream in, then reflect it'));
      return wrap;
    }

    if (!manual) {
      // Everything arriving is already reflected: no add control, nothing to pick.
      wrap.append(
        element(
          'div',
          'node-hint',
          'reflects everything arriving — switch Reflect to “Picked pieces” to choose instead',
        ),
      );
      return wrap;
    }

    if (picked.length > 0) {
      // Five rows in the card; the rest scroll inside the same list.
      const list = element('div', 'node-pick-list');
      for (const piece of picked) {
        const row = element('div', 'node-trim-row');
        row.append(element('span', 'node-trim-key', piece.label));
        const drop = element('button', 'node-trim-x', '×');
        drop.title = `Stop reflecting ${piece.label}`;
        drop.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();
          this.dropPick(graphNode.id, piece.key);
        });
        row.append(drop);
        row.addEventListener('pointerenter', () =>
          this.options.onHoverMirror?.(graphNode.id, [piece.key]),
        );
        row.addEventListener('pointerleave', () => this.options.onHoverMirror?.(graphNode.id, null));
        list.append(row);
      }
      wrap.append(list);
    } else {
      wrap.append(element('div', 'node-hint', 'nothing picked yet — choose pieces in the drawing'));
    }

    const pick = element('button', 'node-trim-pick', 'Mirror in drawing');
    pick.title = 'Pick the pieces to reflect in the viewport (X or Esc ends)';
    if (!this.options.onPickMirror) {
      pick.disabled = true;
      pick.title = 'Mirror picking needs the window that holds the drawing';
    } else {
      pick.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.options.onPickMirror?.(graphNode.id);
      });
    }
    wrap.append(pick);
    return wrap;
  }

  /**
   * What a merge or boolean step leaves out. The pieces are picked in the drawing (`Exclude in
   * drawing`); an excluded piece is not deleted — it passes through the step exactly as drawn,
   * which is how a centre line or a construction line stays clear of the weld.
   */
  private buildExcludeControls(graphNode: GraphNode): HTMLElement {
    const wrap = element('div', 'node-edges');
    const graph = this.store.current;
    const pieces = pickablePieces(graph, graphNode.id, '0');
    const excluded = pieces.filter((piece) => piece.picked);
    const plural = (count: number): string => (count === 1 ? 'piece' : 'pieces');

    const head = element('div', 'node-edges-head');
    head.append(
      element(
        'span',
        'node-edges-count',
        pieces.length === 0
          ? 'nothing wired in yet'
          : excluded.length === 0
            ? `all ${pieces.length} ${plural(pieces.length)} take part`
            : `${excluded.length} of ${pieces.length} left out`,
      ),
    );
    const clear = element('button', 'node-edges-reset', 'clear');
    clear.title = 'Put every piece back into the step';
    clear.disabled = excluded.length === 0;
    clear.addEventListener('click', (event) => {
      event.stopPropagation();
      this.setPicks(graphNode.id, []);
    });
    head.append(clear);
    wrap.append(head);

    if (pieces.length === 0) {
      wrap.append(element('div', 'node-hint', 'wire a shape into A or B first'));
      return wrap;
    }

    if (excluded.length > 0) {
      const list = element('div', 'node-pick-list');
      for (const piece of excluded) {
        const row = element('div', 'node-trim-row');
        row.append(element('span', 'node-trim-key', piece.label));
        const drop = element('button', 'node-trim-x', '×');
        drop.title = `Put ${piece.label} back into the step`;
        drop.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();
          this.togglePick(graphNode.id, piece.key, false);
        });
        row.append(drop);
        row.addEventListener('pointerenter', () =>
          this.options.onHoverExclude?.(graphNode.id, [piece.key]),
        );
        row.addEventListener('pointerleave', () => this.options.onHoverExclude?.(graphNode.id, null));
        list.append(row);
      }
      wrap.append(list);
    } else {
      wrap.append(element('div', 'node-hint', 'nothing left out — the whole A + B is used'));
    }

    const pick = element('button', 'node-trim-pick', 'Exclude in drawing');
    pick.title = 'Click the pieces to leave out of this step (X or Esc ends)';
    if (!this.options.onPickExclude) {
      pick.disabled = true;
      pick.title = 'Excluding needs the window that holds the drawing';
    } else {
      pick.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.options.onPickExclude?.(graphNode.id);
      });
    }
    wrap.append(pick);
    return wrap;
  }

  /**
   * Where the mirror step reflects, in words — including the flip an axis-aligned edge implies
   * (`horizontal → flips Y`), which is the common case and the one worth reading at a glance.
   */
  private axisSummary(graphNode: GraphNode): string {
    const value = graphNode.refs?.['axis'];
    if (value) {
      const graph = this.store.current;
      const match = refOptions(graph, graphNode.id, (id) => evaluateNode(graph, id, '0')).find(
        (option) => option.value === value,
      );
      const axis = mirrorAxis(graph, graphNode.id, '0');
      const kind = axis ? axisKind(axis) : 'free';
      const flip =
        kind === 'horizontal'
          ? ' · horizontal → flips Y'
          : kind === 'vertical'
            ? ' · vertical → flips X'
            : ' · angled axis';
      return `axis: ${match?.label ?? value}${flip}`;
    }
    const angle = Math.round(graphNode.params['angle'] ?? 90);
    return `axis: ${angle}° through the origin — pick an edge to use it as the axis`;
  }

  /** Replacing the whole pick list, as an undoable graph edit. */
  private setPicks(nodeId: string, keys: readonly string[]): void {
    this.store.update((graph) => {
      const node = graph.nodes.find((candidate) => candidate.id === nodeId);
      if (!node) return;
      if (keys.length > 0) node.picks = [...keys];
      else delete node.picks;
    });
    this.options.onHoverMirror?.(nodeId, null);
    this.options.onHoverExclude?.(nodeId, null);
  }

  /** Toggles one piece into or out of the mirror selection. */
  private togglePick(nodeId: string, key: string, picked: boolean): void {
    this.store.update((graph) => {
      const node = graph.nodes.find((candidate) => candidate.id === nodeId);
      if (!node) return;
      const next = new Set(node.picks ?? []);
      if (picked) next.add(key);
      else next.delete(key);
      if (next.size > 0) node.picks = [...next];
      else delete node.picks;
    });
  }

  private dropPick(nodeId: string, key: string): void {
    this.togglePick(nodeId, key, false);
    this.options.onHoverMirror?.(nodeId, null);
  }

  /**
   * The trimmed list, big enough to read. It opens **inside the node panel** so the drawing stays
   * visible next to it — the whole point is watching the hover highlight light the span up.
   */
  private openTrimWindow(graphNode: GraphNode): void {
    const doc = this.root.ownerDocument;
    doc.querySelector('.trim-window')?.remove();

    const overlay = element('div', 'trim-window');
    const card = element('div', 'trim-window-card');
    const title = element('h2', 'trim-window-title', 'Trimmed edges');
    const body = element('div', 'trim-window-list');
    const actions = element('div', 'trim-window-actions');
    const close = element('button', 'tool primary', 'Close');
    actions.append(close);
    card.append(title, body, actions);
    overlay.append(card);
    this.root.append(overlay);

    const unsubscribe = this.store.onChange(() => rebuild());
    const dismiss = (): void => {
      unsubscribe();
      doc.removeEventListener('keydown', onKey, true);
      this.options.onHoverTrim?.(graphNode.id, null);
      overlay.remove();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      dismiss();
    };
    close.addEventListener('click', dismiss);
    overlay.addEventListener('pointerdown', (event) => {
      if (event.target === overlay) dismiss();
    });
    doc.addEventListener('keydown', onKey, true);

    const rebuild = (): void => {
      const trimmed = trimEdges(this.store.current, graphNode.id, '0').filter((edge) => !edge.kept);
      title.textContent = trimmed.length === 0 ? 'Nothing trimmed' : `Trimmed edges — ${trimmed.length}`;
      body.replaceChildren();
      if (trimmed.length === 0) {
        body.append(element('div', 'node-hint', 'Every span is back on the stream.'));
        return;
      }
      for (const edge of trimmed) {
        const row = element('div', 'node-trim-row');
        row.append(element('span', 'node-trim-key', edge.label));
        row.append(element('span', 'node-trim-source', edge.source));
        const cancel = element('button', 'node-trim-x', '×');
        cancel.title = `Put ${edge.label} back`;
        cancel.addEventListener('click', () => this.restoreTrim(graphNode.id, edge.key));
        row.append(cancel);
        row.addEventListener('pointerenter', () => this.options.onHoverTrim?.(graphNode.id, [edge.key]));
        row.addEventListener('pointerleave', () => this.options.onHoverTrim?.(graphNode.id, null));
        body.append(row);
      }
    };

    rebuild();
  }

  /**
   * Frames every step in the canvas. The canvas is unbounded, so this is the way back after
   * dragging something far into negative or large coordinates.
   */
  private fitView(): void {
    const cards = [...this.world.querySelectorAll<HTMLElement>('.node')];
    if (cards.length === 0) return;
    const left = Math.min(...cards.map((card) => card.offsetLeft));
    const top = Math.min(...cards.map((card) => card.offsetTop));
    const right = Math.max(...cards.map((card) => card.offsetLeft + card.offsetWidth));
    const bottom = Math.max(...cards.map((card) => card.offsetTop + card.offsetHeight));

    const width = Math.max(1, right - left);
    const height = Math.max(1, bottom - top);
    const rect = this.canvas.getBoundingClientRect();
    const padding = 40;
    // Frame inside the strip that the insert rail leaves free.
    const rail = RAIL_WIDTH + 12;
    const usable = Math.max(1, rect.width - rail);
    const scale = Math.max(
      MIN_ZOOM,
      Math.min(
        MAX_ZOOM,
        Math.min((usable - padding * 2) / width, (rect.height - padding * 2) / height),
      ),
    );
    this.setView(
      scale,
      rail + (usable - width * scale) / 2 - left * scale,
      (rect.height - height * scale) / 2 - top * scale,
    );
  }

  private drawEdges(): void {
    if (this.scene) return;
    const worldRect = this.world.getBoundingClientRect();
    this.svg.setAttribute('width', String(this.canvas.clientWidth));
    this.svg.setAttribute('height', String(this.canvas.clientHeight));
    this.svg.replaceChildren();

    const wire = (fromPort: Element, toPort: Element, extra?: string): void => {
      const a = fromPort.getBoundingClientRect();
      const b = toPort.getBoundingClientRect();
      const x1 = (a.left + a.width / 2 - worldRect.left) / this.scale;
      const y1 = (a.top + a.height / 2 - worldRect.top) / this.scale;
      const x2 = (b.left + b.width / 2 - worldRect.left) / this.scale;
      const y2 = (b.top + b.height / 2 - worldRect.top) / this.scale;

      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', wirePath({ x: x1, y: y1 }, { x: x2, y: y2 }));
      path.setAttribute('class', extra ? `node-edge ${extra}` : 'node-edge');
      this.svg.append(path);
      return;
    };

    // Value wires first, so the stream sits on top where they cross.
    for (const node of this.store.current.nodes) {
      if (!node.values) continue;
      for (const [paramId, sourceId] of Object.entries(node.values)) {
        const fromPort = this.world.querySelector<HTMLElement>(
          `.node[data-id="${sourceId}"] .port-value-out`,
        );
        const toPort = this.world.querySelector<HTMLElement>(
          `.node[data-id="${node.id}"] .port-in[data-param="${paramId}"]`,
        );
        if (fromPort && toPort) wire(fromPort, toPort, 'is-value');
      }
    }

    for (const edge of this.store.current.edges) {
      const fromPort = this.world.querySelector<HTMLElement>(
        `.node[data-id="${edge.from}"] .port-out`,
      );
      const toPort = this.world.querySelector<HTMLElement>(
        `.node[data-id="${edge.to}"] ${inputSelector(edge)}`,
      );
      if (!fromPort || !toPort) continue;
      wire(fromPort, toPort);
      const path = this.svg.lastElementChild;
      if (path && this.dropTarget && edgeKey(this.dropTarget) === edgeKey(edge)) {
        path.classList.add('is-drop');
      }
    }
    this.drawLiveWire();
  }
}

// --------------------------------------------------------------------- panel

export type NodePanelMode = 'split' | 'window';

/** `ox/oy/width/height` when a closed polyline is an axis-aligned rectangle. */
function rectangleParams(
  vertices: readonly Vec2[],
  closed: boolean,
): Record<string, number> | null {
  if (!closed) return null;
  const points = [...vertices];
  if (points.length > 1 && points[0]!.equals(points[points.length - 1]!)) points.pop();
  if (points.length !== 4) return null;
  for (let index = 0; index < 4; index++) {
    const a = points[index]!;
    const b = points[(index + 1) % 4]!;
    if (Math.abs(a.y - b.y) > 1e-6 && Math.abs(a.x - b.x) > 1e-6) return null;
  }
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const ox = Math.min(...xs);
  const oy = Math.min(...ys);
  const width = Math.max(...xs) - ox;
  const height = Math.max(...ys) - oy;
  if (width <= 1e-6 || height <= 1e-6) return null;
  return { ox, oy, width, height };
}

/**
 * Starter steps from drawn geometry — deliberately conservative: lines, circles and axis-aligned
 * rectangles map exactly, everything else is counted and left as it is. The caller only uses this
 * for a preview, so a rough result costs nothing.
 */
export function nodesFromDrawing(entities: readonly Entity[]): {
  graph: NodeGraph;
  made: number;
  skipped: number;
} {
  const steps: GraphNode[] = [];
  let skipped = 0;

  for (const entity of entities) {
    if (entity.type === 'line') {
      if (entity.a.distanceTo(entity.b) <= 1e-6) continue;
      const delta = entity.b.sub(entity.a);
      const node = createNode('line', 0, 0);
      Object.assign(node.params, {
        ox: entity.a.x,
        oy: entity.a.y,
        length: delta.len(),
        angle: (delta.angle() * 180) / Math.PI,
      });
      steps.push(node);
    } else if (entity.type === 'circle') {
      const node = createNode('circle', 0, 0);
      Object.assign(node.params, {
        ox: entity.center.x,
        oy: entity.center.y,
        radius: entity.radius,
      });
      steps.push(node);
    } else if (entity.type === 'polyline') {
      const plate = rectangleParams(entity.vertices, entity.closed);
      if (!plate) {
        skipped += 1;
        continue;
      }
      const node = createNode('rect', 0, 0);
      Object.assign(node.params, plate);
      steps.push(node);
    } else {
      skipped += 1;
    }
  }

  steps.forEach((node, index) => {
    node.x = 32;
    node.y = 24 + index * 240;
  });
  const output = createNode('emit', 32, 24 + steps.length * 240);
  const edges: GraphEdge[] = steps.map((node, index) => ({
    from: node.id,
    to: index + 1 < steps.length ? steps[index + 1]!.id : output.id,
  }));

  return { graph: { nodes: [...steps, output], edges }, made: steps.length, skipped };
}

/** Evaluates `graph` and commits the result as one undoable step. Returns the count. */
export function applyNodeGraph(editor: CadEditor, graph: NodeGraph): number {
  const entities: Entity[] = evaluateGraph(graph, editor.doc.currentLayerId);
  if (entities.length === 0) return 0;
  const tx = new Transaction();
  for (const entity of entities) tx.add(withNewId(entity));
  editor.commit(tx, 'PROCEDURE');
  return entities.length;
}

/**
 * Installs the procedural node editor into the LK chrome.
 * Split mode embeds it beside the viewport; window mode opens a secondary window
 * that edits the same graph over a BroadcastChannel. Returns the graph store so the
 * project layer can save and restore the graph with the drawing.
 */
export function installNodePanel(
  editor: CadEditor,
  renderer?: CadRenderer,
): { store: GraphStore } | null {
  const panelHost = document.getElementById('nodes-panel');
  const toolbar = document.getElementById('node-buttons') ?? document.getElementById('tool-buttons');
  if (!panelHost || !toolbar) return null;

  const store = new GraphStore(blankGraph(), 'host');
  let mode: NodePanelMode = 'split';
  let visible = false;
  let popup: Window | null = null;

  const applyGraph = (): void => {
    const count = applyNodeGraph(editor, store.current);
    // The preview is a preview: once the geometry is real, leave the committed styling visible.
    if (count > 0) {
      editor.setNodePreview([]);
      view.setEntityCount(0);
    }
    editor.message(
      count === 0
        ? 'NODES  Nothing to apply — link a source into Output.'
        : `NODES  Applied ${count} entities to the drawing.`,
    );
  };

  /** Generates steps from the drawing, but only after the user confirms — the result is rough. */
  const generateFromDrawing = (): void => {
    const all = editor.doc.all();
    const scope =
      editor.selection.size > 0 ? all.filter((entity) => editor.selection.has(entity.id)) : all;
    if (scope.length === 0) {
      editor.message('NODES  Nothing drawn to generate from.');
      return;
    }

    void askChoice({
      title: 'Generate nodes from the drawing?',
      message:
        `This replaces the current node graph with steps made from ${scope.length} object${scope.length === 1 ? '' : 's'}. ` +
        'Lines, circles and axis-aligned rectangles come across exactly; arcs, text, hatches and ' +
        'dimensions stay flat. The drawing itself is not touched and the result is a preview until ' +
        'you press Apply.',
      choices: [
        { id: 'generate', label: 'Generate', primary: true },
        { id: 'cancel', label: 'Cancel' },
      ],
    }).then((choice) => {
      if (choice !== 'generate') return;
      const report = nodesFromDrawing(scope);
      if (report.made === 0) {
        editor.message('NODES  Nothing in this drawing has a node equivalent yet.');
        return;
      }
      store.replace(report.graph);
      setVisible(true);
      const flat = report.skipped > 0 ? `, ${report.skipped} left flat` : '';
      editor.message(
        `NODES  ${report.made} steps generated${flat} — preview only, press Apply to commit`,
      );
    });
  };

  const viewportHost = document.getElementById('viewport');
  const picker: RefPicker | null = viewportHost
    ? createRefPicker(editor, viewportHost, {
        graph: () => store.current,
        onPick: (request, value) => {
          store.update((graph) => {
            const node = graph.nodes.find((candidate) => candidate.id === request.nodeId);
            if (!node) return;
            node.refs ??= {};
            node.refs[request.refId] = value;
          });
          editor.message(`PICK  ${request.label} ← ${value}`);
        },
      })
    : null;

  const trimPicker: TrimPicker | null = viewportHost
    ? createTrimPicker(editor, viewportHost, {
        graph: () => store.current,
        onToggle: (nodeId, key, remove) => {
          store.update((graph) => {
            const node = graph.nodes.find((candidate) => candidate.id === nodeId);
            if (!node) return;
            const hidden = new Set(node.hidden ?? []);
            if (remove) hidden.add(key);
            else hidden.delete(key);
            if (hidden.size > 0) node.hidden = [...hidden];
            else delete node.hidden;
          });
        },
      })
    : null;

  const trimHighlighter = viewportHost
    ? createTrimHighlighter(editor, viewportHost, { graph: () => store.current })
    : null;

  const mirrorPicker: MirrorPicker | null = viewportHost
    ? createMirrorPicker(editor, viewportHost, {
        graph: () => store.current,
        onToggle: (nodeId, key, picked) => {
          store.update((graph) => {
            const node = graph.nodes.find((candidate) => candidate.id === nodeId);
            if (!node) return;
            const next = new Set(node.picks ?? []);
            if (picked) next.add(key);
            else next.delete(key);
            if (next.size > 0) node.picks = [...next];
            else delete node.picks;
          });
          editor.message(`MIRROR  ${picked ? 'reflecting' : 'dropped'} ${key}`);
        },
      })
    : null;

  const mirrorHighlighter = viewportHost
    ? createMirrorHighlighter(editor, viewportHost, { graph: () => store.current })
    : null;

  const excludePicker: ExcludePicker | null = viewportHost
    ? createExcludePicker(editor, viewportHost, {
        graph: () => store.current,
        onToggle: (nodeId, key, excluded) => {
          store.update((graph) => {
            const node = graph.nodes.find((candidate) => candidate.id === nodeId);
            if (!node) return;
            const next = new Set(node.picks ?? []);
            if (excluded) next.add(key);
            else next.delete(key);
            if (next.size > 0) node.picks = [...next];
            else delete node.picks;
          });
          const label =
            pickablePieces(store.current, nodeId, editor.doc.currentLayerId).find(
              (piece) => piece.key === key,
            )?.label ?? key;
          editor.message(`STEP  ${label} ${excluded ? 'left out of' : 'back in'} the step`);
        },
      })
    : null;

  const excludeHighlighter = viewportHost
    ? createExcludeHighlighter(editor, viewportHost, { graph: () => store.current })
    : null;

  // The node ↔ shape link: hovering or picking a card glows its geometry, the finder walks back.
  const linkGlow = viewportHost ? createLinkGlow(editor, viewportHost) : null;

  const findPicker: FindPicker | null = viewportHost
    ? createFindPicker(editor, viewportHost, {
        graph: () => store.current,
        onFind: (nodeId) => {
          view.selectAndReveal(nodeId);
          // A node window needs telling; the drawing window already moved.
          store.sendFocus(nodeId);
        },
      })
    : null;

  const highlightNodes = (nodeIds: readonly string[] | null): void => {
    if (!linkGlow) return;
    if (!nodeIds || nodeIds.length === 0) {
      linkGlow.show(null);
      return;
    }
    const entities = nodeIds.flatMap((id) => nodeOwnEntities(store.current, id, editor.doc.currentLayerId));
    linkGlow.show(entities);
  };

  const view = new NodeEditorView(panelHost, store, {
    role: 'host',
    onApply: applyGraph,
    onGenerate: generateFromDrawing,
    onClose: () => setVisible(false),
    onPickRef: (request) => picker?.start(request),
    onPickTrim: (nodeId) => trimPicker?.start(nodeId),
    onHoverTrim: (nodeId, keys) => trimHighlighter?.show(nodeId, keys),
    onPickMirror: (nodeId) => mirrorPicker?.start(nodeId),
    onHoverMirror: (nodeId, keys) => mirrorHighlighter?.show(nodeId, keys),
    onPickExclude: (nodeId) => excludePicker?.start(nodeId),
    onHoverExclude: (nodeId, keys) => excludeHighlighter?.show(nodeId, keys),
    onHighlightNodes: highlightNodes,
    onFindShape: () => findPicker?.start('*'),
  });

  const sync = (): void => {
    const entities = evaluateGraph(store.current, editor.doc.currentLayerId);
    editor.setNodePreview(entities);
    view.setEntityCount(entities.length);
  };

  store.onChange(sync);
  store.onCommand((command) => {
    // Picking happens where the drawing is, so a request from the separate window brings this
    // one to the front and arms the picker here; the result travels back as a graph update.
    if (command.kind === 'apply') {
      applyGraph();
      return;
    }
    window.focus();
    if (command.kind === 'pick') picker?.start(command.request);
    else if (command.kind === 'mirror') mirrorPicker?.start(command.nodeId);
    else if (command.kind === 'exclude') excludePicker?.start(command.nodeId);
    else if (command.kind === 'find') findPicker?.start('*');
    else if (command.kind === 'highlight') highlightNodes(command.nodeIds);
    else if (command.kind === 'hover') {
      // A row hovered in another window lights the same overlay it would light here.
      const keys = command.keys;
      const nodeId = command.nodeId;
      trimHighlighter?.show(nodeId, command.target === 'trim' ? keys : null);
      mirrorHighlighter?.show(nodeId, command.target === 'mirror' ? keys : null);
      excludeHighlighter?.show(nodeId, command.target === 'exclude' ? keys : null);
    } else trimPicker?.start(command.nodeId);
  });

  const toggleButton = iconButton('nodes', 'Nodes', 'Show or hide the procedural node editor');
  // The word first, then the glyph — the group reads "Nodes" rather than a bare icon.
  toggleButton.classList.add('with-label');
  toggleButton.prepend(element('span', 'tool-label', 'Nodes'));
  toggleButton.addEventListener('click', () => setVisible(!visible));

  const splitButton = iconButton('split', 'Split view', 'Embed the node editor beside the viewport');
  splitButton.addEventListener('click', () => {
    // Choosing a host is a request to see the editor, so it also turns it on.
    mode = 'split';
    visible = true;
    applyMode();
  });

  const windowButton = iconButton(
    'window',
    'Separate window',
    'Open the node editor in a secondary window',
  );
  windowButton.addEventListener('click', () => {
    mode = 'window';
    visible = true;
    applyMode();
    // A blocked popup must not leave the button looking dead: fall back to the embedded panel.
    if (!popup) {
      mode = 'split';
      applyMode();
      editor.message('NODES  The browser blocked the separate window — the editor opened in split view.');
    }
  });

  const group = element('div', 'button-group');
  group.append(toggleButton, splitButton, windowButton);
  toolbar.append(group);

  // ------------------------------------------------------- splitter (resize)

  const splitter = document.getElementById('nodes-splitter');
  const WIDTH_KEY = 'lakaran.nodes-panel-width';
  const DEFAULT_WIDTH = 470;
  const MIN_PANEL = 260;

  /** Panel width in pixels, clamped so the viewport always keeps usable room. */
  const clampWidth = (width: number): number => {
    const stage = panelHost!.parentElement;
    const stageWidth = stage?.clientWidth ?? window.innerWidth;
    const max = Math.max(MIN_PANEL, stageWidth - 320);
    return Math.round(Math.min(Math.max(width, MIN_PANEL), max));
  };

  const setPanelWidth = (width: number, remember = true): void => {
    const clamped = clampWidth(width);
    panelHost!.style.setProperty('--nodes-width', `${clamped}px`);
    if (remember) {
      try {
        localStorage.setItem(WIDTH_KEY, String(clamped));
      } catch {
        /* private mode: the width just is not remembered */
      }
    }
    renderer?.refreshSize();
  };

  const storedWidth = ((): number => {
    try {
      const raw = Number(localStorage.getItem(WIDTH_KEY));
      return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_WIDTH;
    } catch {
      return DEFAULT_WIDTH;
    }
  })();
  setPanelWidth(storedWidth, false);

  function attachSplitter(): void {
    if (!splitter) return;

    let drag: { startX: number; startWidth: number } | null = null;

    const onMove = (event: PointerEvent): void => {
      if (!drag) return;
      // Dragging left widens the panel, because the panel sits on the right.
      setPanelWidth(drag.startWidth - (event.clientX - drag.startX));
    };

    const onUp = (event: PointerEvent): void => {
      if (!drag) return;
      drag = null;
      splitter!.classList.remove('is-dragging');
      try {
        splitter!.releasePointerCapture(event.pointerId);
      } catch {
        /* capture already gone */
      }
      splitter!.removeEventListener('pointermove', onMove);
      splitter!.removeEventListener('pointerup', onUp);
      splitter!.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      renderer?.refreshSize();
    };

    splitter.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      drag = { startX: event.clientX, startWidth: panelHost!.getBoundingClientRect().width };
      splitter!.classList.add('is-dragging');
      try {
        splitter!.setPointerCapture(event.pointerId);
      } catch {
        /* no active pointer (synthetic event): the window listeners still track the drag */
      }
      document.body.style.cursor = 'col-resize';
      splitter!.addEventListener('pointermove', onMove);
      splitter!.addEventListener('pointerup', onUp);
      splitter!.addEventListener('pointercancel', onUp);
    });

    // Double-click restores the default split.
    splitter.addEventListener('dblclick', () => setPanelWidth(DEFAULT_WIDTH));

    // Keyboard resizing, so it works without a mouse.
    splitter.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 64 : 16;
      if (event.key === 'ArrowLeft') setPanelWidth(panelHost!.getBoundingClientRect().width + step);
      else if (event.key === 'ArrowRight') setPanelWidth(panelHost!.getBoundingClientRect().width - step);
      else if (event.key === 'Home') setPanelWidth(DEFAULT_WIDTH);
      else return;
      event.preventDefault();
    });

    splitter.tabIndex = 0;
  }

  /** Keeps the panel usable when the window shrinks. */
  const onWindowResize = (): void => {
    if (panelHost!.hidden) return;
    const current = panelHost!.getBoundingClientRect().width;
    const clamped = clampWidth(current);
    if (clamped !== Math.round(current)) panelHost!.style.setProperty('--nodes-width', `${clamped}px`);
    renderer?.refreshSize();
  };
  window.addEventListener('resize', onWindowResize);

  function setVisible(next: boolean): void {
    visible = next;
    applyMode();
  }

  function applyMode(): void {
    const embedded = visible && mode === 'split';
    panelHost!.hidden = !embedded;
    panelHost!.setAttribute('aria-hidden', String(!embedded));
    if (splitter) splitter.hidden = !embedded;
    if (embedded) view.refresh();

    if (visible && mode === 'window') openPopup();
    else closePopup();

    toggleButton.classList.toggle('active', visible);
    splitButton.classList.toggle('active', mode === 'split');
    windowButton.classList.toggle('active', mode === 'window');
    sync();

    // Force a layout flush, then re-measure the canvas against the new viewport width.
    void panelHost!.getBoundingClientRect();
    renderer?.refreshSize();
  }

  function openPopup(): void {
    if (popup && !popup.closed) {
      popup.focus();
      return;
    }
    popup = window.open('nodes.html', 'lakaran-nodes', 'popup=yes,width=1040,height=780');
    popup?.focus();
  }

  function closePopup(): void {
    if (popup && !popup.closed) popup.close();
    popup = null;
  }

  window.addEventListener('beforeunload', () => {
    closePopup();
    store.dispose();
    window.removeEventListener('resize', onWindowResize);
  });

  attachSplitter();
  applyMode();
  Object.assign(window as unknown as Record<string, unknown>, { __lkNodes: { store, view, mode } });
  return { store };
}

// ----------------------------------------------------------- secondary window

/** Boots the standalone node editor used by `nodes.html`. */
export function bootNodeWindow(root: HTMLElement): void {
  const store = new GraphStore(emptyGraph(), 'client');
  const view = new NodeEditorView(root, store, {
    role: 'client',
    onApply: () => store.sendApply(),
    // This window has no drawing: the main window arms the picker and answers on the channel.
    onPickRef: (request) => store.sendPick(request),
    onPickTrim: (nodeId) => store.sendTrim(nodeId),
    onPickMirror: (nodeId) => store.sendMirror(nodeId),
    onPickExclude: (nodeId) => store.sendExclude(nodeId),
    onFindShape: () => store.sendFind(),
    onHighlightNodes: (nodeIds) => store.sendHighlight(nodeIds),
    // This window has no drawing: hovering a row lights the pieces in the main window.
    onHoverTrim: (nodeId, keys) => store.sendHover('trim', nodeId, keys),
    onHoverMirror: (nodeId, keys) => store.sendHover('mirror', nodeId, keys),
    onHoverExclude: (nodeId, keys) => store.sendHover('exclude', nodeId, keys),
  });

  const sync = (): void => {
    view.refresh();
    view.setEntityCount(evaluateGraph(store.current).length);
  };

  store.onChange(sync);
  store.onCommand((command) => {
    // The drawing window found a shape: bring that card forward here.
    if (command.kind === 'focus') view.selectAndReveal(command.nodeId);
  });
  store.requestSync();
  sync();

  // The main window can be reloaded while this one stays open. Re-sync on focus: hand over the
  // work this window holds, or ask for the host's graph when this one is still empty.
  window.addEventListener('focus', () => {
    if (store.current.nodes.length > 1 || store.current.edges.length > 0) store.share();
    else store.requestSync();
  });

  Object.assign(window as unknown as Record<string, unknown>, {
    __lkNodes: { store, view },
  });
}

const popupRoot = typeof document === 'undefined' ? null : document.getElementById('nodes-root');
if (popupRoot) bootNodeWindow(popupRoot);





